import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  useSearchParams,
  useNavigate,
  useParams,
  Link,
} from "react-router-dom";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import Button from "../components/ui/button";
import Modal from "../components/ui/modal";
import CardColor from "../assets/images/icons/card-icon.png";
import CreditCardGeneric from "../assets/images/icons/credit-card-generic-icon.png";
import SpinNotifyIcon from "../assets/images/icons/spin-notify-icon.svg";
import ScratchNotifyIcon from "../assets/images/icons/scratch-notify-icon.svg";
import SpinTitleIcon from "../assets/images/icons/spin-title-icon.svg";
import ScratchTitleIcon from "../assets/images/icons/scratch-title-icon.svg";
import CircleIcon from "../assets/images/backgrounds/circle.png";
import VisaCard from "../assets/images/backgrounds/visa-card-1.png";
import {
  UserIcon,
  CreditCardIcon,
  CashbackIcon,
  CopyIcon,
} from "../components/icons";
import { useLanguage } from "../context/LanguageContext";
import { useClient } from "../context/ClientContext";
import { useCards } from "../context/CardContext";
import { useTenant } from "../context/TenantContext";
import { useNPS } from "../hooks/useNPS";
import FeedbackModal from "../components/ui/FeedbackModal";
import apiService from "../services/api.service";
import logger from "../utils/logger";
import CryptoJS from "crypto-js";
import Navigation from "../components/ui/navigation";
import Loader from "../components/ui/loader";
import { useABTest } from "../context/ABTestContext";
import GuidedTour from "../components/ui/GuidedTour";
import ABTestPopup from "../components/ui/ABTestPopup";
import DOMPurify from "dompurify";
import { sanitizeLogMessage } from '../utils/sanitize'
import { isCashbackSummaryEnabled } from '../utils/tenantFeatures'

/** `t()` reads tenant CMS `v3` only; missing keys return the key path (truthy), so `t(k) || fallback` never works. */
function tOr(t, key, fallback) {
  const v = t(key);
  return v === key ? fallback : v;
}

function pickFirstNonEmptyString(...candidates) {
  for (const v of candidates) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return null;
}

const PROFILE_TEXT_HTML_PURIFY = {
  ADD_ATTR: ["class", "style"],
};

/** Scratch / CHALLENGE campaign label from rewardinfo (field names vary by API). */
function getScratchCampaignNameFromReward(reward) {
  if (!reward) return null;
  const r = reward;
  return pickFirstNonEmptyString(
    r.displayName,
    r.campaignName,
    r.campaign_name,
    r.campaignTitle,
    r.campaign_title,
    r.offerName,
    r.offer_name,
    r.gamificationCampaignName,
    r.scratchCampaignName,
    r.challengeCampaignName,
    r.challengeName,
    r.challenge_name,
    r.merchantName,
  );
}

function getLocalizedText(value, tenant) {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value !== "object") return null;

  const languageContent = tenant?.tenantlanguagecontent || {};
  const languageId = languageContent.languageid;
  const languageCode = languageContent.languagecode;
  const languageTranslate = languageContent.languagecodetranslate;

  return pickFirstNonEmptyString(
    value[languageCode],
    value[languageTranslate],
    value[String(languageId)],
    value.en,
    value.ja,
    ...Object.values(value).filter(
      (text) => typeof text === "string" || typeof text === "number",
    ),
  );
}

function getRewardOverviewConfig(reward) {
  const overview = reward?.rewardOverview;

  if (!overview) return {};
  if (typeof overview === "object") return overview;

  if (typeof overview === "string") {
    try {
      const parsed = JSON.parse(overview);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  return {};
}

function getRewardOverviewImage(reward) {
  const overview = getRewardOverviewConfig(reward);
  return pickFirstNonEmptyString(
    overview.logoImage,
    reward?.gamificationRewardLogoImage,
  );
}

export default function MyProfileWithTabs() {
  const { t } = useLanguage();
  const { client, isAuthenticated, hydrateFromSignIn, refreshToken } =
    useClient();
  const { cards, loading: cardsLoading, fetchCards } = useCards();
  const { tenant } = useTenant();
  const showCashbackSummary = isCashbackSummaryEnabled(tenant);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkEligibility } = useNPS();
  const {
    refetchAssignments,
    tourActive,
    startTour,
    isSessionDismissed,
    dismissForSession,
    isShownThisSession,
    markShownThisSession,
    crossTabVersion,
    acquireABTestLock,
    markABTestHandledThisSession,
    releaseABTestLock,
    isCurrentABTestTab,
    isABTestClaimOwner,
    isABTestHandledThisSession,
  } = useABTest();
  const [newVariantAssignment, setNewVariantAssignment] = useState(null);
  const [showNPS, setShowNPS] = useState(false);
  const [cashbackTabViewed, setCashbackTabViewed] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [unenrollConfirmModal, setUnenrollConfirmModal] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: null,
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [cBBaseReward, setCBBaseReward] = useState(0);
  const [cBBonusReward, setCBBonusReward] = useState(0);
  const [bonusCashback, setBonusCashback] = useState(0);
  const [firstExp, setFirstExp] = useState(null);
  const [availableSpin, setAvailableSpin] = useState(0);
  const [availableScratch, setAvailableScratch] = useState(0);
  const [campaignId, setCampaignId] = useState("");
  const [scratchCampaignId, setScratchCampaignId] = useState("");
  const [scratchCampaignBanner, setScratchCampaignBanner] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedTransactionId, setCopiedTransactionId] = useState(false);
  const [copiedVoxId, setCopiedVoxId] = useState(false);
  const [rewardsList, setRewardsList] = useState([]);
  const [merchantsList, setMerchantsList] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [cardAdded, setCardAdded] = useState(false);
  const [showCardAliasModal, setShowCardAliasModal] = useState(false);
  const [pendingAliasModal, setPendingAliasModal] = useState(false);
  const [aliasCardNumber, setAliasCardNumber] = useState("");
  const [cardAlias, setCardAlias] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [cashbackFilter, setCashbackFilter] = useState("");
  const [isExpressMiniLoading, setIsExpressMiniLoading] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [spinCampaignRewardsById, setSpinCampaignRewardsById] = useState({});
  const [activeGamificationType, setActiveGamificationType] = useState(null);
  const [gamCaretLeftPx, setGamCaretLeftPx] = useState(null);
  const gamTabStripRef = useRef(null);
  const gamContentRef = useRef(null);
  const campaignBannerTrackRef = useRef(null);
  const campaignBannerScrollRafRef = useRef(null);
  const campaignBannerIndexLockUntilRef = useRef(0);
  const campaignBannerPausedRef = useRef(false);
  const [campaignBannerActiveIndex, setCampaignBannerActiveIndex] =
    useState(0);
  const [campaignBannerPaused, setCampaignBannerPaused] = useState(false);
  const isCardAddProcessRunning = useRef(false);
  const pendingNewVariantRef = useRef(null);
  const pendingTourRef = useRef(false);
  const { tenanturlname } = useParams();

  useEffect(() => {
    if (!newVariantAssignment) return;
    if (
      isABTestHandledThisSession(newVariantAssignment.test_id) ||
      !isCurrentABTestTab(newVariantAssignment.test_id)
    ) {
      setNewVariantAssignment(null);
    }
  }, [newVariantAssignment, crossTabVersion, isABTestHandledThisSession, isCurrentABTestTab]);

  const showNewVariantPopup = useCallback((assignment) => {
    if (!assignment) return;
    try {
      localStorage.setItem(`ab_np_${client?.externalUserId}_${assignment.test_id}`, '1');
    } catch {}
    markShownThisSession(assignment.test_id);
    setNewVariantAssignment(assignment);
  }, [client?.externalUserId, markShownThisSession]);

  /** Banners for carousel — same keys as getCampaigns (mobile-spin-banner-*, spin-banner-*). */
  const campaignBannerSlides = useMemo(() => {
    const languageCode = tenant?.tenantlanguagecontent?.languagecode || "en";
    if (!Array.isArray(allCampaigns) || allCampaigns.length === 0) return [];
    const mobileKey = `mobile-spin-banner-${languageCode}`;
    const desktopKey = `spin-banner-${languageCode}`;
    const fallbackImg = require("../assets/images/backgrounds/campaign-1.png");
    return allCampaigns
      .map((campaign, idx) => {
        const config = campaign.campaign_config;
        if (!config || typeof config !== "object") return null;
        const mobileRaw = config[mobileKey];
        const desktopRaw = config[desktopKey];
        if (!mobileRaw && !desktopRaw) return null;
        const resolvedCampaignId =
          campaign.campaignId ||
          campaign.campaign_id ||
          campaign.campaignid ||
          campaign.id;
        return {
          key: `${campaign.type}-${String(resolvedCampaignId || "na")}-${idx}`,
          type: campaign.type,
          campaignId: resolvedCampaignId,
          mobile: mobileRaw || desktopRaw || fallbackImg,
          desktop: desktopRaw || mobileRaw || fallbackImg,
        };
      })
      .filter(Boolean);
  }, [allCampaigns, tenant?.tenantlanguagecontent?.languagecode]);

  const onCampaignBannerScroll = useCallback(() => {
    const el = campaignBannerTrackRef.current;
    if (!el) return;
    if (campaignBannerScrollRafRef.current) {
      cancelAnimationFrame(campaignBannerScrollRafRef.current);
    }
    campaignBannerScrollRafRef.current = requestAnimationFrame(() => {
      if (Date.now() < campaignBannerIndexLockUntilRef.current) return;
      const width = Math.max(el.clientWidth, 1);
      const best = Math.round(el.scrollLeft / width);
      setCampaignBannerActiveIndex(
        Math.max(0, Math.min(best, campaignBannerSlides.length - 1)),
      );
    });
  }, [campaignBannerSlides.length]);

  const pauseCampaignBanner = useCallback(() => {
    campaignBannerPausedRef.current = true;
    setCampaignBannerPaused(true);
  }, []);

  const resumeCampaignBanner = useCallback(() => {
    campaignBannerPausedRef.current = false;
    setCampaignBannerPaused(false);
  }, []);

  const scrollCampaignBannerToIndex = useCallback((el, index, behavior) => {
    const width = Math.max(el?.clientWidth || 0, 1);
    el.scrollTo({ left: width * index, behavior });
  }, []);

  const scrollCampaignBannerTo = useCallback(
    (idx) => {
      const el = campaignBannerTrackRef.current;
      if (!el || campaignBannerSlides.length === 0) return;
      const next = Math.max(0, Math.min(idx, campaignBannerSlides.length - 1));
      campaignBannerIndexLockUntilRef.current = Date.now() + 320;
      setCampaignBannerActiveIndex(next);
      scrollCampaignBannerToIndex(el, next, "smooth");
    },
    [campaignBannerSlides.length, scrollCampaignBannerToIndex],
  );

  const scrollCampaignBannerNext = useCallback(() => {
    const el = campaignBannerTrackRef.current;
    if (!el || campaignBannerSlides.length <= 1) return;
    setCampaignBannerActiveIndex((prev) => {
      const len = campaignBannerSlides.length;
      const atEnd = prev >= len - 1;
      const next = atEnd ? 0 : prev + 1;
      campaignBannerIndexLockUntilRef.current = Date.now() + (atEnd ? 80 : 320);
      scrollCampaignBannerToIndex(el, next, atEnd ? "auto" : "smooth");
      return next;
    });
  }, [campaignBannerSlides.length, scrollCampaignBannerToIndex]);

  useEffect(() => {
    setCampaignBannerActiveIndex(0);
    campaignBannerIndexLockUntilRef.current = 0;
    campaignBannerPausedRef.current = false;
    setCampaignBannerPaused(false);
    const el = campaignBannerTrackRef.current;
    if (el) el.scrollTo({ left: 0, behavior: "auto" });
  }, [campaignBannerSlides.length]);

  useEffect(() => {
    if (campaignBannerSlides.length <= 1 || campaignBannerPaused) return undefined;
    const id = setInterval(() => {
      if (campaignBannerPausedRef.current) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      scrollCampaignBannerNext();
    }, 2500);
    return () => clearInterval(id);
  }, [campaignBannerSlides.length, campaignBannerPaused, scrollCampaignBannerNext]);

  const getCampaignId = useCallback((campaign) => (
    campaign?.campaignId ||
    campaign?.campaign_id ||
    campaign?.campaignid ||
    campaign?.id ||
    ""
  ), []);

  const getCampaignType = useCallback((campaign) => {
    const type = String(campaign?.type || campaign?.gamificationType || "").toUpperCase();
    if (type === "SCRATCH_CARD") return "CHALLENGE";
    return type;
  }, []);

  const getCampaignTitle = useCallback((campaign) => {
    const type = getCampaignType(campaign);
    return (
      campaign?.campaignName ||
      campaign?.campaign_name ||
      campaign?.name ||
      (type === "CHALLENGE"
        ? tOr(t, "myProfile.scratchCard", "Scratch Card")
        : tOr(t, "common.spinCampaignTitle", "Spin Campaign"))
    );
  }, [getCampaignType, t]);

  const getCampaignExpiryRaw = useCallback((campaign) => {
    if (!campaign) return null;
    const candidates = [
      campaign.campaign_end_date,
      campaign.campaignEndDate,
      campaign.campaignExpiryDate,
      campaign.campaign_expiry_date,
      campaign.endDate,
      campaign.expiryDate,
      campaign.end_date,
      campaign.expiry_date,
      campaign.validUntil,
      campaign.valid_until,
      campaign.expiresAt,
      campaign.expires_at,
      campaign.expiry,
      campaign.endTime,
      campaign.end_time,
      campaign.endDateTime,
      campaign.end_datetime,
    ];
    for (const value of candidates) {
      if (value != null && value !== "") return value;
    }
    const config = campaign.campaign_config;
    if (config && typeof config === "object" && !Array.isArray(config)) {
      const configKeys = [
        "endDate",
        "expiryDate",
        "end_date",
        "expiry_date",
        "campaignEndDate",
        "validUntil",
        "expiresAt",
      ];
      for (const key of configKeys) {
        if (config[key] != null && config[key] !== "") return config[key];
      }
    }
    return null;
  }, []);

  const parseCampaignExpiryDate = useCallback((raw) => {
    if (raw == null || raw === "") return null;
    if (raw instanceof Date) {
      return Number.isNaN(raw.getTime()) ? null : raw;
    }
    if (typeof raw === "number") {
      const ms = raw < 1e12 ? raw * 1000 : raw;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof raw === "string") {
      let value = raw.trim();
      if (/^\d{4}-\d{2}-\d{2} /.test(value)) {
        value = value.replace(" ", "T");
      }
      value = value.replace(/(\.\d{3})\d+/, "$1");
      let date = new Date(value);
      if (!Number.isNaN(date.getTime())) return date;

      const match = raw.trim().match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/,
      );
      if (match) {
        date = new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
          Number(match[4]),
          Number(match[5]),
          Number(match[6]),
        );
        return Number.isNaN(date.getTime()) ? null : date;
      }
      return null;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const spinCampaigns = useMemo(() => (
    Array.isArray(allCampaigns)
      ? allCampaigns.filter(
        (campaign) => getCampaignType(campaign) === "SPIN",
      )
      : []
  ), [allCampaigns, getCampaignType]);

  const challengeCampaigns = useMemo(() => (
    Array.isArray(allCampaigns)
      ? allCampaigns.filter(
        (campaign) => getCampaignType(campaign) === "CHALLENGE",
      )
      : []
  ), [allCampaigns, getCampaignType]);

  const availableGamificationTypes = useMemo(() => {
    const types = [];
    if (spinCampaigns.length > 0) types.push("SPIN");
    if (challengeCampaigns.length > 0) types.push("CHALLENGE");
    return types;
  }, [challengeCampaigns.length, spinCampaigns.length]);

  useEffect(() => {
    if (availableGamificationTypes.length === 0) {
      setActiveGamificationType(null);
      return;
    }
    const tenantDefaultType = String(
      tenant?.tenantmasterconfig?.gamification?.defaultType || "",
    ).toUpperCase();
    const normalizedDefault =
      tenantDefaultType === "SCRATCH_CARD" ? "CHALLENGE" : tenantDefaultType;
    if (
      !activeGamificationType ||
      !availableGamificationTypes.includes(activeGamificationType)
    ) {
      setActiveGamificationType(
        availableGamificationTypes.includes(normalizedDefault)
          ? normalizedDefault
          : availableGamificationTypes[0],
      );
    }
  }, [
    activeGamificationType,
    availableGamificationTypes,
    tenant?.tenantmasterconfig?.gamification?.defaultType,
  ]);

  useEffect(() => {
    if (!tenant?.tenantid || !client?.clientId || spinCampaigns.length === 0) {
      setSpinCampaignRewardsById({});
      return undefined;
    }

    let isCancelled = false;

    const fetchSpinCampaignChances = async () => {
      const campaignIds = [
        ...new Set(spinCampaigns.map(getCampaignId).filter(Boolean)),
      ];

      if (campaignIds.length === 0) {
        if (!isCancelled) setSpinCampaignRewardsById({});
        return;
      }

      const entries = await Promise.all(
        campaignIds.map(async (id) => {
          try {
            const response = await apiService.post("/api/gamification/get-reward", {
              tenantid: tenant.tenantid,
              clientid: client.clientId,
              campaignId: id,
            });
            const parsed = Number(response?.asc);
            return [id, {
              availableSpinCount: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
              firstExpiryDate: response?.fxd || null,
              campaignId: response?.ci || id,
            }];
          } catch (error) {
            console.error("Error fetching spin campaign chance count:", error);
            return [id, {
              availableSpinCount: 0,
              firstExpiryDate: null,
              campaignId: id,
            }];
          }
        }),
      );

      if (!isCancelled) {
        setSpinCampaignRewardsById(Object.fromEntries(entries));
      }
    };

    fetchSpinCampaignChances();

    return () => {
      isCancelled = true;
    };
  }, [client?.clientId, getCampaignId, spinCampaigns, tenant?.tenantid]);

  const updateGamificationCaretPosition = useCallback(() => {
    const strip = gamTabStripRef.current;
    const content = gamContentRef.current;
    if (!strip || !content || !activeGamificationType) return;
    const button = strip.querySelector(
      `[data-gam-tab="${activeGamificationType}"]`,
    );
    if (!button) return;
    const contentRect = content.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    setGamCaretLeftPx(buttonRect.left + buttonRect.width / 2 - contentRect.left);
  }, [activeGamificationType]);

  useEffect(() => {
    if (availableGamificationTypes.length <= 1) {
      setGamCaretLeftPx(null);
      return undefined;
    }
    updateGamificationCaretPosition();
    window.addEventListener("resize", updateGamificationCaretPosition);
    return () =>
      window.removeEventListener("resize", updateGamificationCaretPosition);
  }, [availableGamificationTypes.length, updateGamificationCaretPosition]);

  const getCampaignChanceCount = useCallback((campaign, type) => {
    const campaignIdValue = getCampaignId(campaign);
    if (type === "SPIN") {
      const campaignCount = campaignIdValue
        ? spinCampaignRewardsById[campaignIdValue]?.availableSpinCount
        : null;
      if (campaignCount != null) return campaignCount;
      return spinCampaigns.length === 1 ? availableSpin : 0;
    }

    const scratchCount = Number(
      campaign?.scratchChancesCount ?? campaign?.scratch_chances_count,
    );
    return Number.isFinite(scratchCount) && scratchCount >= 0
      ? scratchCount
      : availableScratch;
  }, [
    availableScratch,
    availableSpin,
    getCampaignId,
    spinCampaignRewardsById,
    spinCampaigns.length,
  ]);

  const getCampaignExpiryDate = useCallback((campaign) => {
    if (getCampaignType(campaign) === "SPIN") {
      const campaignIdValue = getCampaignId(campaign);
      const rewardExpiry = campaignIdValue
        ? spinCampaignRewardsById[campaignIdValue]?.firstExpiryDate
        : null;
      return (
        parseCampaignExpiryDate(rewardExpiry) ||
        (spinCampaigns.length === 1 ? parseCampaignExpiryDate(firstExp) : null) ||
        parseCampaignExpiryDate(getCampaignExpiryRaw(campaign))
      );
    }

    return parseCampaignExpiryDate(getCampaignExpiryRaw(campaign));
  }, [
    firstExp,
    getCampaignId,
    getCampaignExpiryRaw,
    getCampaignType,
    parseCampaignExpiryDate,
    spinCampaignRewardsById,
    spinCampaigns.length,
  ]);

  const getCampaignExpiryText = useCallback((campaign) => {
    const expiryDate = getCampaignExpiryDate(campaign);
    if (!expiryDate) return null;
    const diff = expiryDate - new Date();
    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0 && hours > 0) {
      return t("common.expiresInDaysHours")
        .replace("{days}", days)
        .replace("{hours}", hours);
    }
    if (days > 0) {
      return t("common.expiresInDaysOnly").replace("{days}", days);
    }
    if (hours > 0 && minutes > 0) {
      return t("common.expiresInHoursMinutes")
        .replace("{hours}", hours)
        .replace("{minutes}", minutes);
    }
    if (hours > 0) {
      return t("common.expiresInHoursOnly").replace("{hours}", hours);
    }
    if (minutes > 0 && seconds > 0) {
      return t("common.expiresInMinutesSeconds")
        .replace("{minutes}", minutes)
        .replace("{seconds}", seconds);
    }
    if (minutes > 0) {
      return t("common.expiresInMinutesOnly").replace("{minutes}", minutes);
    }
    return t("common.expiresInSecondsOnly").replace("{seconds}", seconds);
  }, [getCampaignExpiryDate, t]);

  // Get max card count from tenant config or default to 5
  const maxCardCount = tenant?.tenantmaxcardcount;

  // No-result image: configurable via masterconfig (filename only), falls back to inline SVG
  const noResultImageFilename = tenant?.tenantmasterconfig?.noresultimage || "";
  const noResultImageUrl = noResultImageFilename.trim()
    ? `${process.env.REACT_APP_CLOUDFRONT}icons/${noResultImageFilename}`
    : null;
  const isCardLimitReached = cards.length >= maxCardCount;

  const isRetryableVisaParamsError = (error) => {
    const status = error?.status || error?.response?.status;
    return error?.isRateLimit || status === 429 || status === 503;
  };

  const getApiErrorStatus = (error) => (
    error?.status || error?.response?.status || error?.data?.status
  );

  const GetVisaParams = async (retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        logger.info("MyProfile - Fetching Visa params", sanitizeLogMessage({
          tenantid: tenant?.tenantid,
          clientid: client?.clientId,
          attempt: attempt + 1,
        }));
        const encryptparams = await apiService.get(
          "/api/cards/enrolleevisaparams",
          {
            params: {
              tenantid: tenant.tenantid,
              clientid: client.clientId,
            },
          },
        );
        return encryptparams;
      } catch (error) {
        logger.error("MyProfile - Visa params request failed", sanitizeLogMessage({
          status: getApiErrorStatus(error),
          code: error?.response?.data?.code || error?.data?.code,
          message: error?.response?.data?.message || error?.data?.message || error?.message,
          tenantid: tenant?.tenantid,
          clientid: client?.clientId,
          attempt: attempt + 1,
        }));
        if (isRetryableVisaParamsError(error) && attempt < retries) {
          await new Promise((r) => setTimeout(r, 10000));
          continue;
        }
        throw error;
      }
    }
  };

  const buildExpressMiniUrl = (params) => {
    const required = [
      "displayemail",
      "visa_key",
      "visa_iv",
      "visa_info1_express",
      "expressmini_url",
      "community_code",
      "cert_version",
    ];
    const missing = required.filter((key) => !params?.[key]);
    if (missing.length > 0) {
      logger.error("MyProfile - Visa params response missing fields", sanitizeLogMessage({
        missing,
        tenantid: tenant?.tenantid,
        clientid: client?.clientId,
      }));
      throw new Error("Visa params response missing required fields");
    }

    const info = `{EUID:${client.externalUserId},PC:${tenant.tenantpromocode},EM:${params.displayemail}}`;

    const key = CryptoJS.enc.Utf8.parse(params.visa_key);
    const iv = CryptoJS.enc.Utf8.parse(params.visa_iv);

    const encryptedInfo = CryptoJS.AES.encrypt(info, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }).toString();

    const langCode =
      tenant?.tenantlanguagecontent?.expressmini_lang_code || "en";

    const encodedInfo = encodeURIComponent(encryptedInfo);
    const encodedInfo1 = encodeURIComponent(params.visa_info1_express);

    return `${params.expressmini_url}${params.community_code}?info=${encodedInfo}&info1=${encodedInfo1}&info2=V${params.cert_version}&lang=${langCode}`;
  };

  const showExpressMiniError = (error) => {
    const status = getApiErrorStatus(error);
    const isAuthError = status === 401 || status === 403;

    setStatusModal({
      open: true,
      type: "error",
      message: isAuthError
        ? tOr(t, "myProfile.sessionRefreshFailed", "Your session has expired. Please sign in again.")
        : t("myProfile.errorInitializeEnrollment"),
    });
  };

  // Handle Express Mini button click - refresh tokens before navigation
  const handleExpressMiniClick = async () => {
    if (isCardLimitReached || !tenant || !client) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cta_click', ctaName: 'card_link' });
    setIsExpressMiniLoading(true);

    try {
      // Refresh tokens before navigating to Express Mini
      const refreshSuccess = await refreshToken();

      if (!refreshSuccess) {
        console.error("MyProfile - Token refresh failed before Express Mini");
        setStatusModal({
          open: true,
          type: "error",
          message: tOr(t, "myProfile.sessionRefreshFailed", "Your session has expired. Please sign in again."),
        });
        return;
      }

      const params = await GetVisaParams();
      const expressMiniUrl = buildExpressMiniUrl(params);

      logger.info("MyProfile - Express Mini URL initialized", sanitizeLogMessage({
        tenantid: tenant?.tenantid,
        clientid: client?.clientId,
      }));
      window.open(expressMiniUrl, "_self");
    } catch (error) {
      logger.error("MyProfile - Error during Express Mini navigation:", sanitizeLogMessage({
        status: getApiErrorStatus(error),
        code: error?.response?.data?.code || error?.data?.code,
        message: error?.response?.data?.message || error?.data?.message || error?.message,
        tenantid: tenant?.tenantid,
        clientid: client?.clientId,
      }));
      showExpressMiniError(error);
    } finally {
      setIsExpressMiniLoading(false);
    }
  };

  // Check NPS eligibility when cashback tab is viewed
  useEffect(() => {
    const checkNPSEligibility = async () => {
      const npsConfig = tenant?.tenantmasterconfig?.nps_config
      if (!npsConfig?.enabled || !npsConfig?.triggers?.cashback_history?.enabled) return
      const currentTab = searchParams.get("tab");
      if (
        currentTab === "cashback" &&
        isAuthenticated &&
        client?.clientId &&
        !cashbackTabViewed
      ) {
        setCashbackTabViewed(true);
        try {
          const result = await checkEligibility("cashback_history");
          if (result?.eligible) {
            setShowNPS(true);
          }
        } catch (error) {
          console.error("NPS eligibility check failed:", error);
        }
      }
    };
    checkNPSEligibility();
  }, [
    searchParams,
    isAuthenticated,
    client?.clientId,
    cashbackTabViewed,
    checkEligibility,
    tenant,
  ]);

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setIsCardModalOpen(true);
  };

  const closeCardModal = () => {
    setIsCardModalOpen(false);
    setSelectedCard(null);
  };

  const handleDeleteClick = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cta_click', ctaName: 'card_delete' });
    setIsCardModalOpen(false);
    setDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    removeCard();
  };

  const handleUnenrollClick = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cta_click', ctaName: 'program_unenroll' });
    setUnenrollConfirmModal(true);
  };

  const handleConfirmUnenroll = () => {
    unenrollClient();
  };

  const closeStatusModal = () => {
    setStatusModal({ open: false, type: null, message: "" });

    // If we have a pending alias modal (after successful card add), open it now
    if (pendingAliasModal) {
      setPendingAliasModal(false);
      setShowCardAliasModal(true);
      return;
    }

    // If alias flow just completed and a new_variant tour is pending, show popup now
    if (pendingTourRef.current && pendingNewVariantRef.current) {
      pendingTourRef.current = false;
      const assignment = pendingNewVariantRef.current;
      pendingNewVariantRef.current = null;
      if (
        !isABTestHandledThisSession(assignment.test_id) &&
        acquireABTestLock(assignment.test_id, "popup")
      ) {
        showNewVariantPopup(assignment);
      }
    }
  };

  const handleRewardClick = (reward) => {
    setSelectedReward(reward);
    setIsRewardModalOpen(true);
  };

  const closeRewardModal = () => {
    setIsRewardModalOpen(false);
    setSelectedReward(null);
  };

  const getGamificationRewardDisplayName = useCallback((reward) => {
    if (!reward?.reward_id) return reward?.merchantName;

    const rewardOverview = getRewardOverviewConfig(reward);
    const configuredDisplayText = getLocalizedText(
      rewardOverview.displayText || reward.gamificationRewardDisplayText,
      tenant,
    );
    if (configuredDisplayText) return configuredDisplayText;

    const gamificationType = String(reward.gamificationType || "").toUpperCase();
    if (gamificationType === "SCRATCH_CARD") {
      return (
        getScratchCampaignNameFromReward(reward) ||
        tOr(t, "myProfile.scratchCard", "Scratch card")
      );
    }
    if (gamificationType === "SPIN") {
      return tOr(t, "myProfile.spinWheel", "Spin wheel");
    }
    return reward.merchantName;
  }, [t, tenant]);

  const getGamificationRewardImage = useCallback((reward) => {
    if (!reward?.reward_id) return reward?.merchantImage || CreditCardGeneric;

    const configuredImage = getRewardOverviewImage(reward);
    if (configuredImage) return configuredImage;

    return CircleIcon;
  }, []);

  const rewardDetailsHeaderImage = useMemo(() => {
    if (!selectedReward) return CreditCardGeneric;
    return getGamificationRewardImage(selectedReward);
  }, [selectedReward, getGamificationRewardImage]);

  const unenrollClient = async () => {
    try {
      setIsLoading(true);
      setUnenrollConfirmModal(false);

      const unenrollObj = {
        clientid: client.clientId,
        tenantid: tenant.tenantid,
      };

      const response = await apiService.put(
        "/api/cards/unenrollclient",
        unenrollObj,
      );

      // Update client context with new data
      if (response) {
        hydrateFromSignIn(response);
      }

      // Refresh cards list
      await fetchCards(client.clientId, tenant.tenantid);

      setStatusModal({
        open: true,
        type: "success",
        message: t("myProfile.successUnenrolled"),
      });
    } catch (error) {
      console.error("Error unenrolling client:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: t("myProfile.errorUnenroll"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const removeCard = async () => {
    try {
      setIsLoading(true);
      setDeleteConfirmModal(false);

      const deleteObj = {
        cardid: selectedCard.cardnumber,
        clientid: client.clientId,
        tenantid: tenant.tenantid,
        id: selectedCard.id,
      };

      const response = await apiService.delete("/api/cards/carddelete", {
        params: deleteObj,
      });

      // Update client context with new data
      if (response) {
        hydrateFromSignIn(response);
      }

      // Refresh cards list
      await fetchCards(client.clientId, tenant.tenantid);

      setStatusModal({
        open: true,
        type: "success",
        message: t("myProfile.successCardRemoved"),
      });

      setSelectedCard(null);
    } catch (error) {
      console.error("Error removing card:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: t("myProfile.errorRemoveCard"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setDefaultCard = async (cardinfo) => {
    try {
      setIsLoading(true);

      const response = await apiService.put("/api/cards/cardsetdefault", {
        clientid: client.clientId,
        tenantid: tenant.tenantid,
        cardnumber: cardinfo.cardnumber,
      });

      // Update client context with new data
      if (response) {
        hydrateFromSignIn(response);
      }

      // Refresh cards list to show updated default card
      await fetchCards(client.clientId, tenant.tenantid);

      setStatusModal({
        open: true,
        type: "success",
        message: t("myProfile.successDefaultCard"),
      });
    } catch (error) {
      console.error("Error setting default card:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: t("myProfile.errorDefaultCard"),
      });
    } finally {
      setIsLoading(false);
    }
  };
  // cashback data in-file (previously in src/data/cashback.json)

  useEffect(() => {
    // Reward APIs only need the stable tenant/client ids. Keeping enrollment
    // state out of this effect prevents reward refetches when Link Card starts
    // or finishes the Express Mini flow.
    if (client?.clientId && tenant?.tenantid) {
      getRewardInfo();
    }
  }, [client?.clientId, tenant?.tenantid]);

  const getRewardInfo = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setIsLoading(true);
      }
      const res = await apiService.get("/api/rewards/rewardinfo", {
        params: {
          tenantid: tenant.tenantid,
          clientid: client.clientId,
        },
      });

      // await apiService.get("/api/rewards/rewardinfo", {
      //   params: {
      //     tenantid: tenant.tenantid,
      //     clientid: client.clientId,
      //   },
      // });

      // const res =   {
      //   "rewards": [
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465240173204918",
      //       "reward_id": null,
      //       "txAmount": "1452.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-08-28T04:48:40.000Z",
      //       "rewardAmount": "435.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-28T04:48:40.000Z",
      //       "createdAt": "2025-09-03T14:23:19.000Z",
      //       "updatedAt": "2025-09-03T14:23:20.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465237152796838",
      //       "reward_id": null,
      //       "txAmount": "968.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-08-25T04:14:39.000Z",
      //       "rewardAmount": "290.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-25T04:14:39.000Z",
      //       "createdAt": "2025-08-31T06:50:09.000Z",
      //       "updatedAt": "2025-08-31T06:50:10.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "paypayカード",
      //       "last_four": "7612"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "385237118175542",
      //       "reward_id": null,
      //       "txAmount": "3850.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-08-25T03:16:57.000Z",
      //       "rewardAmount": "1155.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-25T03:16:57.000Z",
      //       "createdAt": "2025-08-30T15:02:11.000Z",
      //       "updatedAt": "2025-08-30T15:02:12.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "paypayカード",
      //       "last_four": "7612"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "585231184098794",
      //       "reward_id": null,
      //       "txAmount": "2100.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-08-19T05:06:49.000Z",
      //       "rewardAmount": "630.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-19T05:06:49.000Z",
      //       "createdAt": "2025-08-22T19:20:06.000Z",
      //       "updatedAt": "2025-08-22T19:20:07.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "385231161334904",
      //       "reward_id": null,
      //       "txAmount": "968.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-08-19T04:28:53.000Z",
      //       "rewardAmount": "290.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-19T04:28:53.000Z",
      //       "createdAt": "2025-08-23T04:24:45.000Z",
      //       "updatedAt": "2025-08-23T04:24:46.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "paypayカード",
      //       "last_four": "7612"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "VISAC7MDCM0497",
      //       "reward_id": null,
      //       "txAmount": "525.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-08-19T00:00:00.000Z",
      //       "rewardAmount": "157.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-19T00:00:00.000Z",
      //       "createdAt": "2025-10-28T10:03:08.000Z",
      //       "updatedAt": "2025-10-28T10:03:09.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "paypayカード",
      //       "last_four": "7612"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "385216093655414",
      //       "reward_id": null,
      //       "txAmount": "3459.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-08-04T02:36:05.000Z",
      //       "rewardAmount": "1037.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-04T02:36:05.000Z",
      //       "createdAt": "2025-08-07T12:11:04.000Z",
      //       "updatedAt": "2025-08-07T12:11:05.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "585216093167888",
      //       "reward_id": null,
      //       "txAmount": "3490.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-08-04T02:35:16.000Z",
      //       "rewardAmount": "1047.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-08-04T02:35:16.000Z",
      //       "createdAt": "2025-08-07T20:05:47.000Z",
      //       "updatedAt": "2025-08-07T20:05:48.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "paypayカード",
      //       "last_four": "7612"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "585209078986732",
      //       "reward_id": null,
      //       "txAmount": "968.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-07-28T02:11:38.000Z",
      //       "rewardAmount": "290.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-28T02:11:38.000Z",
      //       "createdAt": "2025-08-01T19:43:13.000Z",
      //       "updatedAt": "2025-08-01T19:43:15.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "385209076409353",
      //       "reward_id": null,
      //       "txAmount": "385.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-07-28T02:07:20.000Z",
      //       "rewardAmount": "115.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-28T02:07:20.000Z",
      //       "createdAt": "2025-08-01T19:09:13.000Z",
      //       "updatedAt": "2025-08-01T19:09:14.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465209061587714",
      //       "reward_id": null,
      //       "txAmount": "3240.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-07-28T01:42:38.000Z",
      //       "rewardAmount": "972.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-28T01:42:38.000Z",
      //       "createdAt": "2025-08-01T15:19:27.000Z",
      //       "updatedAt": "2025-08-01T15:19:28.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465209038161915",
      //       "reward_id": null,
      //       "txAmount": "1534.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-07-28T01:03:36.000Z",
      //       "rewardAmount": "460.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-28T01:03:36.000Z",
      //       "createdAt": "2025-08-02T17:58:02.000Z",
      //       "updatedAt": "2025-08-02T17:58:02.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0602316252620250731",
      //       "reward_id": null,
      //       "txAmount": "1300.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/oyYaxD4qcP/860b3e72-8a2f-4b23-b296-4ae87d355825.png",
      //       "txDateUtc": "2025-07-28T00:00:00.000Z",
      //       "rewardAmount": "390.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策A",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Card",
      //       "displayName": null,
      //       "merchantId": "604",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2051,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-28T00:00:00.000Z",
      //       "createdAt": "2025-08-02T05:06:19.000Z",
      //       "updatedAt": "2025-08-02T05:06:20.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/M15ZR3ag0Y/a8669611-be33-4dc7-b4ea-46499ef10a77.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "202511100000225",
      //       "reward_id": null,
      //       "txAmount": "525.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-07-20T12:00:00.000Z",
      //       "rewardAmount": "157.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-20T12:00:00.000Z",
      //       "createdAt": "2025-11-24T15:29:36.000Z",
      //       "updatedAt": "2025-11-24T15:29:37.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "1105112295220250723",
      //       "reward_id": null,
      //       "txAmount": "4444.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/vpwQW9xtng/1c3bce82-6cfd-4e92-b4d2-80c7d59cd67b.png",
      //       "txDateUtc": "2025-07-20T00:00:00.000Z",
      //       "rewardAmount": "1333.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "大阪施策B",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign7Mobile",
      //       "displayName": null,
      //       "merchantId": "605",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 2052,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-07-20T00:00:00.000Z",
      //       "createdAt": "2025-07-25T09:53:32.000Z",
      //       "updatedAt": "2025-07-25T09:53:33.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/dSwzHTIGvW/3bb6ae24-64c1-4781-ac4f-619861ad7d40.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465149111112821",
      //       "reward_id": "RWD000IAoBVla7cQ5eXP",
      //       "txAmount": "1650.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-29T03:05:11.000Z",
      //       "rewardAmount": "500.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "500.00",
      //       "bonusRewardDate": "2025-06-04T12:32:37.000Z",
      //       "bonusExpiry": "2025-06-13T23:41:29.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-06-04T12:32:37.000Z",
      //       "createdAt": "2025-06-03T23:41:29.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305149124862154",
      //       "reward_id": "RWD000IAnuie4WY4M5Vb",
      //       "txAmount": "3300.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-29T03:28:06.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-06-04T12:32:18.000Z",
      //       "bonusExpiry": "2025-06-13T19:25:06.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-06-04T12:32:18.000Z",
      //       "createdAt": "2025-06-03T19:25:05.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "585149152649876",
      //       "reward_id": "RWD000IAnpIbOXmzlKdy",
      //       "txAmount": "5000.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-29T04:14:24.000Z",
      //       "rewardAmount": "500.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "500.00",
      //       "bonusRewardDate": "2025-06-03T18:25:44.000Z",
      //       "bonusExpiry": "2025-06-13T18:02:20.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-06-03T18:25:44.000Z",
      //       "createdAt": "2025-06-03T18:02:20.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "楽天カード",
      //       "last_four": "4035"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "585149343347505",
      //       "reward_id": "RWD000IAmKVkycmIGocI",
      //       "txAmount": "2990.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-29T09:32:14.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-06-02T22:54:57.000Z",
      //       "bonusExpiry": "2025-06-12T18:25:34.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-06-02T22:54:57.000Z",
      //       "createdAt": "2025-06-02T18:25:34.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "楽天カード",
      //       "last_four": "4035"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "TRNST00198331",
      //       "reward_id": null,
      //       "txAmount": "480.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/WZON5G8wxX/4d75d5b0-7718-4e39-947e-af85b98443a8.png",
      //       "txDateUtc": "2025-05-30T12:00:00.000Z",
      //       "rewardAmount": "144.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "鉄道",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "VisaTransit",
      //       "displayName": null,
      //       "merchantId": "545",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1952,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-30T12:00:00.000Z",
      //       "createdAt": "2025-09-22T04:12:20.000Z",
      //       "updatedAt": "2025-09-22T04:12:21.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/0DOEYnde7f/60f9cab3-5b5c-4d6d-927b-3bbaf6fd4f7c.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305145314629195",
      //       "reward_id": "RWD000IAgP6PBnJPeeeb",
      //       "txAmount": "4938.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-25T08:44:22.000Z",
      //       "rewardAmount": "500.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "500.00",
      //       "bonusRewardDate": "2025-05-29T22:34:25.000Z",
      //       "bonusExpiry": "2025-06-08T21:10:55.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-05-29T22:34:25.000Z",
      //       "createdAt": "2025-05-29T21:10:54.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "585149152649876",
      //       "reward_id": null,
      //       "txAmount": "5000.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BQlv2x1It1/84724a2b-94ff-4f3d-b77a-3a29ff380453.png",
      //       "txDateUtc": "2025-05-29T04:14:24.000Z",
      //       "rewardAmount": "1500.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンB",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Mobile",
      //       "displayName": null,
      //       "merchantId": "547",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1951,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-29T04:14:24.000Z",
      //       "createdAt": "2025-06-03T18:02:20.000Z",
      //       "updatedAt": "2025-06-03T18:02:21.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/tHCd3oY5hK/be1c4cbe-f139-4b7d-8419-e0485bafd7ec.png",
      //       "target_group": "MERCHANT",
      //       "alias": "楽天カード",
      //       "last_four": "4035"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305149124862154",
      //       "reward_id": null,
      //       "txAmount": "3300.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BQlv2x1It1/84724a2b-94ff-4f3d-b77a-3a29ff380453.png",
      //       "txDateUtc": "2025-05-29T03:28:06.000Z",
      //       "rewardAmount": "990.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンB",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Mobile",
      //       "displayName": null,
      //       "merchantId": "547",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1951,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-29T03:28:06.000Z",
      //       "createdAt": "2025-06-03T19:25:05.000Z",
      //       "updatedAt": "2025-06-03T19:25:06.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/tHCd3oY5hK/be1c4cbe-f139-4b7d-8419-e0485bafd7ec.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465149111112821",
      //       "reward_id": null,
      //       "txAmount": "1650.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BQlv2x1It1/84724a2b-94ff-4f3d-b77a-3a29ff380453.png",
      //       "txDateUtc": "2025-05-29T03:05:11.000Z",
      //       "rewardAmount": "495.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンB",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Mobile",
      //       "displayName": null,
      //       "merchantId": "547",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1951,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-29T03:05:11.000Z",
      //       "createdAt": "2025-06-03T23:41:29.000Z",
      //       "updatedAt": "2025-06-03T23:41:31.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/tHCd3oY5hK/be1c4cbe-f139-4b7d-8419-e0485bafd7ec.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305148039116288",
      //       "reward_id": null,
      //       "txAmount": "5328.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BZEi9tt408/6a5d40b2-fc3a-43dd-a79a-a474c2c6acf8.png",
      //       "txDateUtc": "2025-05-28T01:05:11.000Z",
      //       "rewardAmount": "1500.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンA",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Card",
      //       "displayName": null,
      //       "merchantId": "543",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1950,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-28T01:05:11.000Z",
      //       "createdAt": "2025-06-01T20:29:29.000Z",
      //       "updatedAt": "2025-06-01T20:29:30.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/eBOaW4ojEv/0f76c3c6-89de-48e2-b90f-a9a2c7b1f5c8.png",
      //       "target_group": "MERCHANT",
      //       "alias": "楽天カード",
      //       "last_four": "4035"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305145314629195",
      //       "reward_id": null,
      //       "txAmount": "4938.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BZEi9tt408/6a5d40b2-fc3a-43dd-a79a-a474c2c6acf8.png",
      //       "txDateUtc": "2025-05-25T08:44:22.000Z",
      //       "rewardAmount": "1481.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンA",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Card",
      //       "displayName": null,
      //       "merchantId": "543",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1950,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-25T08:44:22.000Z",
      //       "createdAt": "2025-05-29T21:10:55.000Z",
      //       "updatedAt": "2025-05-29T21:10:56.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/eBOaW4ojEv/0f76c3c6-89de-48e2-b90f-a9a2c7b1f5c8.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305139209393820",
      //       "reward_id": "RWD000IAViiiUk7Nnvav",
      //       "txAmount": "3960.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-19T05:48:59.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-05-22T22:49:38.000Z",
      //       "bonusExpiry": "2025-06-01T20:21:49.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-05-22T22:49:38.000Z",
      //       "createdAt": "2025-05-22T20:21:48.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0800403445120250519",
      //       "reward_id": "RWD000IAS85IL41ObjWd",
      //       "txAmount": "1101.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-15T00:00:00.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-05-21T07:00:25.000Z",
      //       "bonusExpiry": "2025-05-31T03:44:21.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-05-21T07:00:25.000Z",
      //       "createdAt": "2025-05-21T03:44:21.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "REPLY2025120801",
      //       "reward_id": null,
      //       "txAmount": "5492.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/ReuLL8er6f/cf46a898-e4da-4ce7-a167-764aa62d1425.png",
      //       "txDateUtc": "2025-05-19T05:33:24.000Z",
      //       "rewardAmount": "1500.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "エポスカード",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Epos",
      //       "displayName": null,
      //       "merchantId": "549",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1956,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-05-19T05:33:24.000Z",
      //       "createdAt": "2025-12-08T19:28:06.000Z",
      //       "updatedAt": "2025-12-08T19:28:20.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/mX7msan2nJ/099210ec-a5ff-4c7f-8d81-a315859bc34b.png",
      //       "target_group": "MERCHANT",
      //       "alias": "エポスカード",
      //       "last_four": "3246"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "1304008777220250514",
      //       "reward_id": "RWD000IAKWuk2aAIMbNi",
      //       "txAmount": "6470.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-05-11T00:00:00.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-05-15T22:18:31.000Z",
      //       "bonusExpiry": "2025-05-25T11:48:10.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-05-15T22:18:31.000Z",
      //       "createdAt": "2025-05-15T11:48:10.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0603811089520250425",
      //       "reward_id": "RWD000H9sG6AzeUYboOz",
      //       "txAmount": "3960.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-21T00:00:00.000Z",
      //       "rewardAmount": "100.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "100.00",
      //       "bonusRewardDate": "2025-04-27T03:04:01.000Z",
      //       "bonusExpiry": "2025-05-06T21:59:54.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-04-27T03:04:01.000Z",
      //       "createdAt": "2025-04-26T21:59:54.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0700205356220250429",
      //       "reward_id": null,
      //       "txAmount": "473.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BQlv2x1It1/84724a2b-94ff-4f3d-b77a-3a29ff380453.png",
      //       "txDateUtc": "2025-04-26T00:00:00.000Z",
      //       "rewardAmount": "141.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンB",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Mobile",
      //       "displayName": null,
      //       "merchantId": "547",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1951,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-04-26T00:00:00.000Z",
      //       "createdAt": "2025-04-30T07:43:31.000Z",
      //       "updatedAt": "2025-04-30T08:03:19.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/tHCd3oY5hK/be1c4cbe-f139-4b7d-8419-e0485bafd7ec.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0700205356320250429",
      //       "reward_id": null,
      //       "txAmount": "1538.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BQlv2x1It1/84724a2b-94ff-4f3d-b77a-3a29ff380453.png",
      //       "txDateUtc": "2025-04-26T00:00:00.000Z",
      //       "rewardAmount": "171.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンB",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Mobile",
      //       "displayName": null,
      //       "merchantId": "547",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1951,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-04-26T00:00:00.000Z",
      //       "createdAt": "2025-04-30T10:33:19.000Z",
      //       "updatedAt": "2025-04-30T10:33:46.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/tHCd3oY5hK/be1c4cbe-f139-4b7d-8419-e0485bafd7ec.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0603112185520250424",
      //       "reward_id": "RWD000H9qaYzKEcQbMgD",
      //       "txAmount": "3498.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-21T00:00:00.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-04-25T22:41:43.000Z",
      //       "bonusExpiry": "2025-05-05T19:23:46.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-04-25T22:41:43.000Z",
      //       "createdAt": "2025-04-25T19:23:46.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465107139157371",
      //       "reward_id": "RWD000H9ndkeXIEV0Z5C",
      //       "txAmount": "3300.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-17T03:51:55.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-04-23T21:44:16.000Z",
      //       "bonusExpiry": "2025-05-03T20:52:26.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-04-23T21:44:16.000Z",
      //       "createdAt": "2025-04-23T20:52:26.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0700505448620250422",
      //       "reward_id": "RWD000H9nYzGQP2efDDC",
      //       "txAmount": "1990.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-19T00:00:00.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-04-23T21:43:57.000Z",
      //       "bonusExpiry": "2025-05-03T19:39:42.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-04-23T21:43:57.000Z",
      //       "createdAt": "2025-04-23T19:39:41.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465107146033813",
      //       "reward_id": "RWD000H9nYrQPcnvh01N",
      //       "txAmount": "1650.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-17T04:03:23.000Z",
      //       "rewardAmount": "5.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "5.00",
      //       "bonusRewardDate": "2025-04-23T21:43:36.000Z",
      //       "bonusExpiry": "2025-05-03T19:37:46.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-04-23T21:43:36.000Z",
      //       "createdAt": "2025-04-23T19:37:45.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0603811089520250425",
      //       "reward_id": null,
      //       "txAmount": "3960.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BQlv2x1It1/84724a2b-94ff-4f3d-b77a-3a29ff380453.png",
      //       "txDateUtc": "2025-04-21T00:00:00.000Z",
      //       "rewardAmount": "1188.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンB",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Mobile",
      //       "displayName": null,
      //       "merchantId": "547",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1951,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-04-21T00:00:00.000Z",
      //       "createdAt": "2025-04-26T21:59:54.000Z",
      //       "updatedAt": "2025-04-28T16:40:07.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/tHCd3oY5hK/be1c4cbe-f139-4b7d-8419-e0485bafd7ec.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465107146033813",
      //       "reward_id": null,
      //       "txAmount": "1650.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BZEi9tt408/6a5d40b2-fc3a-43dd-a79a-a474c2c6acf8.png",
      //       "txDateUtc": "2025-04-17T04:03:23.000Z",
      //       "rewardAmount": "495.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンA",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Card",
      //       "displayName": null,
      //       "merchantId": "543",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1950,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-04-17T04:03:23.000Z",
      //       "createdAt": "2025-04-23T19:37:45.000Z",
      //       "updatedAt": "2025-04-23T19:39:14.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/eBOaW4ojEv/0f76c3c6-89de-48e2-b90f-a9a2c7b1f5c8.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "465107139157371",
      //       "reward_id": null,
      //       "txAmount": "3300.00",
      //       "merchantImage": "https://voxprod-cdn.pulseid.com/BZEi9tt408/6a5d40b2-fc3a-43dd-a79a-a474c2c6acf8.png",
      //       "txDateUtc": "2025-04-17T03:51:55.000Z",
      //       "rewardAmount": "990.00",
      //       "merchantTranslation": [
      //         {
      //           "name": "キャンペーンA",
      //           "languageId": 529,
      //           "languageCode": "ja",
      //           "languageLabel": "Japanese",
      //           "languageNative": "日本語"
      //         }
      //       ],
      //       "merchantName": "Campaign6Card",
      //       "displayName": null,
      //       "merchantId": "543",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": 1950,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": null,
      //       "gamificationType": null,
      //       "txOrderDateUtc": "2025-04-17T03:51:55.000Z",
      //       "createdAt": "2025-04-23T20:52:26.000Z",
      //       "updatedAt": "2025-04-23T20:54:00.000Z",
      //       "offerLogo": "https://voxprod-cdn.pulseid.com/eBOaW4ojEv/0f76c3c6-89de-48e2-b90f-a9a2c7b1f5c8.png",
      //       "target_group": "MERCHANT",
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "305098145552492",
      //       "reward_id": "RWD000H9YCKov2bYDAVe",
      //       "txAmount": "1780.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-08T04:02:35.000Z",
      //       "rewardAmount": "100.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "CREDIT SUCCESSFUL",
      //       "offerId": null,
      //       "bonusAmount": "100.00",
      //       "bonusRewardDate": "2025-04-13T21:52:19.000Z",
      //       "bonusExpiry": "2025-04-23T17:13:45.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": "2025-04-13T21:52:19.000Z",
      //       "createdAt": "2025-04-13T17:13:45.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     },
      //     {
      //       "customerId": "VOXJAPAN1700000628754",
      //       "transactionId": "0700205356320250429",
      //       "reward_id": "RWD000H9xdM06DLyVLuR",
      //       "txAmount": "1538.00",
      //       "merchantImage": null,
      //       "txDateUtc": "2025-04-26T00:00:00.000Z",
      //       "rewardAmount": "0.00",
      //       "merchantTranslation": null,
      //       "merchantName": "Any merchant offer",
      //       "displayName": null,
      //       "merchantId": "0",
      //       "rewardStatus": "QUALIFIED",
      //       "offerId": null,
      //       "bonusAmount": "0.00",
      //       "bonusRewardDate": null,
      //       "bonusExpiry": "2025-05-10T10:33:19.000Z",
      //       "gamificationType": "SPIN",
      //       "txOrderDateUtc": null,
      //       "createdAt": "2025-04-30T10:33:19.000Z",
      //       "updatedAt": "2025-12-08T18:23:38.000Z",
      //       "offerLogo": null,
      //       "target_group": null,
      //       "alias": "スタシアカード",
      //       "last_four": "7004"
      //     }
      //   ],
      //   "totalCount": 42,
      //   "pageCount": 1,
      //   "limit": 100000,
      //   "page": 0
      // }

      const rewards = res.rewards;
      const merchantList = res.merchantList;

      let baseReward = 0;
      let bonusReward = 0;

      await rewards?.map((rew, index) => {
        if (
          rew.rewardStatus === "REWARD ISSUED" ||
          rew.rewardStatus === "CREDIT PENDING" ||
          rew.rewardStatus === "CREDIT SUCCESSFUL"
        ) {
          rew.reward_id
            ? (bonusReward = bonusReward + parseFloat(rew.bonusAmount))
            : (baseReward = baseReward + parseFloat(rew.rewardAmount));
        }
        //

        const currentLangCode =
          tenant?.tenantlanguagecontent?.languagecode || "en";
        if (currentLangCode !== "en") {
          // console.log("r.merchantTranslation", rew.merchantTranslation)
          if (rew.merchantTranslation) {
            try {
              const obj = JSON.parse(rew.merchantTranslation);
              obj?.map((merchantNameMul) => {
                if (merchantNameMul?.languageCode === currentLangCode) {
                  rew.merchantName = merchantNameMul.name;
                  rewards[index] = rew;
                }
              });
            } catch (error) {
              // Silently handle translation parsing error
            }
          }
        }
      });
      setCBBaseReward(baseReward);
      setCBBonusReward(bonusReward);

      setRewardsList(rewards);

      const currentLangCode =
        tenant?.tenantlanguagecontent?.languagecode || "en";
      if (currentLangCode !== "en") {
        merchantList?.map((merchant, index) => {
          if (merchant.merchantTranslation) {
            try {
              const obj = JSON.parse(merchant.merchantTranslation);
              obj?.map((merchantNameMul) => {
                if (merchantNameMul?.languageCode === currentLangCode) {
                  merchant.merchantName = merchantNameMul.name;
                  merchantList[index] = merchant;
                }
              });
            } catch (error) {
              // Silently handle translation parsing error
            }
          }
        });
      }

      setMerchantsList(merchantList);

      const res2 = await apiService.post("/api/gamification/get-reward", {
        tenantid: tenant.tenantid,
        clientid: client.clientId,
      });
      const res3 = await apiService.get(
        "/api/gamification/get-scratch-chances",
        {
          params: {
            tenantid: tenant.tenantid,
            clientid: client.clientId,
          },
        },
      );

      setBonusCashback(res2.bc);

      // const fxDate = new Date(new Date(res2.fxd).toLocaleDateString(tenant?.tenantmasterconfig.common.local_code, { year: "numeric", day: '2-digit', month: '2-digit' }));
      // const fxDateString = `${fxDate.getDate()}/${((fxDate.getMonth()) + 1) > 10 ? (fxDate.getMonth()) + 1 : '0' + (fxDate.getMonth() + 1)}/${fxDate.getFullYear()}`

      if (res2.fxd) {
        setFirstExp(res2.fxd);
      } else {
        setFirstExp(null);
      }

      setAvailableSpin(res2.asc);
      setAvailableScratch(res3?.data?.scratchChancesCount || 0);

      setCampaignId(res2.ci);
      setScratchCampaignId(res3?.data?.campaignId || "");
    } catch (error) {
      console.error("Error fetching reward info:", error);
    } finally {
      if (!skipLoadingState) {
        setIsLoading(false);
      }
    }
  };

  // Fetch campaign banners
  const getCampaigns = async () => {
    try {
      const res = await apiService.get("/api/gamification/get-campaigns", {
        params: {
          tenantid: tenant?.tenantid,
          clientid: client?.clientId,
        },
      });

      if (res && res.length > 0) {
        const languageCode =
          tenant?.tenantlanguagecontent?.languagecode || "en";

        // Store full campaign list so the banner can slide through all active campaigns.
        setAllCampaigns(res);

        // Filter CHALLENGE type campaign
        const challengeCampaign = res.find(
          (campaign) => campaign.type === "CHALLENGE",
        );

        if (challengeCampaign && challengeCampaign.campaign_config) {
          const mobileBannerKey = `mobile-spin-banner-${languageCode}`;
          const desktopBannerKey = `spin-banner-${languageCode}`;
          const mobileUrl = challengeCampaign.campaign_config[mobileBannerKey];
          const desktopUrl =
            challengeCampaign.campaign_config[desktopBannerKey];

          setScratchCampaignBanner({
            mobile: mobileUrl,
            desktop: desktopUrl,
          });
        }
      } else {
        setAllCampaigns([]);
      }
    } catch (error) {
      console.error("❌ Error fetching campaigns:", error);
      // Silently fail, will use fallback images
    }
  };

  // Fetch campaigns when tenant and client are available
  useEffect(() => {
    if (tenant?.tenantid && client?.clientId) {
      getCampaigns();
    }
  }, [tenant?.tenantid, client?.clientId]);

  const copyToClipboard = (text, setSpecificCopiedState) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        if (setSpecificCopiedState) {
          setSpecificCopiedState(true);
        }
        setTimeout(() => {
          setCopied(false);
          if (setSpecificCopiedState) {
            setSpecificCopiedState(false);
          }
        }, 1000);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Card addition process - handles callback from Express Mini
  const CardAddProcess = useCallback(async () => {
    const infoParam = searchParams.get("info");
    if (!infoParam) return;

    // Prevent duplicate execution
    if (isCardAddProcessRunning.current) {
      return;
    }

    // Wait for tenant and client to be loaded
    if (!tenant?.tenantid || !client?.clientId) {
      return;
    }

    try {
      isCardAddProcessRunning.current = true;
      setIsEnrolling(true);

      // Parse the return parameters from Express Mini
      const returnparams = infoParam.replace("{", "").replace("}", "");
      const params = returnparams.split(",");

      const cid = params[0]?.replace("CID:", "").trim();
      const euid = params[1]?.replace("EUID:", "").trim();
      const success = params[2]?.replace("SUCCESS:", "").trim();
      const uid = params[3]?.replace("UID:", "").trim();

      // Fetch current cards from database to get accurate count
      const currentCardsResponse = await apiService.get(
        "/api/cards/cardgetall",
        {
          params: {
            clientid: client.clientId,
            tenantid: tenant.tenantid,
          },
        },
      );

      const existingCards = currentCardsResponse || [];
      const cardExists = existingCards.some((card) => card.cardnumber === cid);

      if (cardExists) {
        setCardAdded(true);
        await fetchCards(client.clientId, tenant.tenantid);
        setStatusModal({
          open: true,
          type: "success",
          message:
            t("myProfile.cardAddedSuccess") || "Card added successfully!",
        });
        setIsEnrolling(false);
        return;
      }

      if (success === "true") {
        // Get Visa card user details
        const userdata = await apiService.post("/api/cards/getvisacarduser", {
          tenantid: tenant.tenantid,
          clientid: client.clientId,
          userkey: client.externalUserId,
        });

        const carddetails = userdata.enrollmentRecord?.cardDetails;
        if (!carddetails) {
          throw new Error("Card details not found");
        }

        // Find the specific card details
        const currentCard = carddetails.find((card) => card.CardId === cid);
        if (!currentCard) {
          throw new Error("Card not found in enrollment record");
        }

        // Prepare card object
        const cardobj = {
          isdefault: existingCards.length === 0 ? 1 : 0,
          clientid: client.clientId,
          tenantid: tenant.tenantid,
          UserKey: client.externalUserId,
          UserId: uid,
          CardId: cid,
          ReferenceId: currentCard.ServiceKey,
          CardLast4: currentCard.CardLast4,
        };

        // Create card in system
        const response = await apiService.post(
          "/api/cards/cardcreate",
          cardobj,
        );

        if (response.code === "OWERRADDCARD0001") {
          setStatusModal({
            open: true,
            type: "error",
            message: t("myProfile.cardAlreadyRegistered"),
          });
          setIsEnrolling(false);
        } else {
          // Set cardAdded flag FIRST to prevent duplicate useEffect triggers
          setCardAdded(true);

          // Update client context with new data
          if (response) {
            hydrateFromSignIn(response);
          }

          // Refresh cards list
          await fetchCards(client.clientId, tenant.tenantid);

          // AB Test: re-fetch assignments; store for new_variant — will show after alias flow
          // Skip if ABTestManager already showed it this session (e.g. user added a second card)
          if (
            tenant?.tenantmasterconfig?.ab_test?.enabled &&
            tenant?.tenantmasterconfig?.ab_test?.new?.enabled !== false &&
            (tenant?.tenantmasterconfig?.ab_test?.new?.trigger ?? "card_enrollment") === "card_enrollment"
          ) {
            const freshAssignments = await refetchAssignments(client?.languageId);
            const newVariant = freshAssignments?.find(
              (a) =>
                a.segment_type === "new_variant" &&
                !a.is_popup_discarded &&
                !a.is_tour_completed &&
                !a.card_enrollment_popup_shown &&
                !isSessionDismissed(a.test_id) &&
                !isABTestHandledThisSession(a.test_id) &&
                (!isShownThisSession(a.test_id) || isABTestClaimOwner(a.test_id)),
            );
            if (newVariant) {
              pendingNewVariantRef.current = newVariant;
            }
          }

          // Prepare card alias modal data (will open after success modal closes)
          setAliasCardNumber(cid);
          const defaultAliasText =
            tenant?.tenantcontentconfig?.cards?.card_alias || "Card";
          setCardAlias(`${defaultAliasText} ${response.cardEnrollId}`);
          setPendingAliasModal(true);

          // Show success modal first
          setStatusModal({
            open: true,
            type: "success",
            message:
              t("myProfile.cardAddedSuccess") || "Card added successfully!",
          });
          setIsEnrolling(false);
        }
      } else {
        // Enrollment was not successful
        setStatusModal({
          open: true,
          type: "error",
          message: t("myProfile.cardEnrollmentUniversalError"),
          renderAsHtml: true,
        });
        setIsEnrolling(false);
      }
    } catch (error) {
      console.error("Error in CardAddProcess:", error);

      // Check if error response contains OWERRADDCARD0001 error code
      const errorCode =
        error.response?.data?.code ||
        error.data?.code ||
        error.data?.data?.code ||
        error.code;

      if (errorCode === "OWERRADDCARD0001") {
        setStatusModal({
          open: true,
          type: "error",
          message: t("myProfile.cardAlreadyRegistered"),
        });
        setIsEnrolling(false);
      } else {
        setStatusModal({
          open: true,
          type: "error",
          message: t("myProfile.cardEnrollmentUniversalError"),
          renderAsHtml: true,
        });
        setIsEnrolling(false);
      }
    } finally {
      // Clean up URL parameter BEFORE releasing the lock so any useEffect
      // re-triggered by the flag reset cannot see ?info= still in the URL.
      if (searchParams.get("info")) {
        searchParams.delete("info");
        navigate({ search: searchParams.toString() }, { replace: true });
      }
      isCardAddProcessRunning.current = false;
    }
  }, [
    tenant,
    client,
    searchParams,
    hydrateFromSignIn,
    fetchCards,
    t,
    navigate,
    refetchAssignments,
    isSessionDismissed,
    isABTestHandledThisSession,
    isShownThisSession,
    isABTestClaimOwner,
  ]);

  // Trigger CardAddProcess when conditions are met
  useEffect(() => {
    const hasInfoParam = searchParams.get("info");

    if (
      tenant?.tenantid &&
      client?.clientId &&
      hasInfoParam &&
      !cardAdded &&
      !isCardAddProcessRunning.current
    ) {
      CardAddProcess();
    }
  }, [
    tenant?.tenantid,
    client?.clientId,
    searchParams,
    cardAdded,
    CardAddProcess,
  ]);

  const validateCardAlias = (alias) => {
    // Check if input is string and not empty
    if (typeof alias !== "string" || !alias) {
      return {
        isValid: false,
        error: t("myProfile.aliasEmpty"),
      };
    }

    const trimmedAlias = alias.trim();

    // Check length constraints (1-10 characters)
    if (trimmedAlias.length === 0 || trimmedAlias.length > 15) {
      return {
        isValid: false,
        error: t("myProfile.aliasLength"),
      };
    }

    // Block < and > characters
    if (trimmedAlias.includes("<") || trimmedAlias.includes(">")) {
      return {
        isValid: false,
        error: t("myProfile.aliasSpecialChars"),
      };
    }

    // Check for common malicious patterns
    const maliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+=/i, // onclick=, onload=, etc.
      /alert\(/i,
      /eval\(/i,
    ];

    if (maliciousPatterns.some((pattern) => pattern.test(trimmedAlias))) {
      return {
        isValid: false,
        error: t("myProfile.aliasInvalidChars"),
      };
    }

    // Validate allowed characters: letters from any language, numbers, spaces, and specific special characters
    // Using Unicode property \p{L} to match letters from any language
    // Allowed special characters: . - _ ' ' ( ) , & + # / :
    const allowedPattern = /^[\p{L}\p{Nd}\s.\-_''(),&#+/:]+$/u;

    if (!allowedPattern.test(trimmedAlias)) {
      return {
        isValid: false,
        error: t("myProfile.aliasInvalidChars"),
      };
    }

    return {
      isValid: true,
      error: "",
    };
  };

  const handleSaveCardAlias = async () => {
    // Validate alias before saving
    const validation = validateCardAlias(cardAlias);
    if (!validation.isValid) {
      setStatusModal({
        open: true,
        type: "error",
        message: validation.error,
      });
      return;
    }

    try {
      setIsLoading(true);

      // Update card alias via API
      await apiService.put("/api/cards/setalias", {
        cardnumber: aliasCardNumber,
        alias: cardAlias,
        clientid: client.clientId,
        tenantid: tenant.tenantid,
      });

      // Refresh cards list
      await fetchCards(client.clientId, tenant.tenantid);

      // Update selected card if editing from card detail modal
      if (selectedCard && selectedCard.cardnumber === aliasCardNumber) {
        setSelectedCard((prev) => ({ ...prev, cardalias: cardAlias }));
      }

      setShowCardAliasModal(false);
      // Flag tour to show after this result notification closes (new_variant only)
      if (pendingNewVariantRef.current) pendingTourRef.current = true;
      setStatusModal({
        open: true,
        type: "success",
        message:
          t("myProfile.cardAliasUpdated") || "Card name updated successfully!",
      });
    } catch (error) {
      console.error("Error updating card alias:", error);
      setShowCardAliasModal(false);
      // Flag tour to show after this result notification closes (new_variant only)
      if (pendingNewVariantRef.current) pendingTourRef.current = true;
      setStatusModal({
        open: true,
        type: "error",
        message:
          t("myProfile.cardAliasUpdateError") || "Failed to update card name.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="max-w-md mx-auto md:max-w-none p-4 md:px-0 space-y-6 pb-24 xl:pb-4 ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
          <div className="contents md:block space-y-3">
            {/* My profile */}
            <div className="box-1 row-start-1 md:row-auto">
              <div className="header-section space-y-3 bg-blue-500/15 px-4 py-3 rounded-2xl shadow-sm">
                {/* Header */}
                <div className="main-title">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <UserIcon className="w-7 h-7 text-theme-secondary" />
                      <h1 className="text-base font-extrabold text-theme-secondary">
                        {t("myProfile.title")}
                      </h1>
                    </div>
                  </div>
                </div>

                {/* User ID */}
                <div className="border-t border-t-blue-700/10 pt-3">
                  <p className="text-xs font-extrabold text-theme-secondary text-left">
                    {t("myProfile.userIdLabel")}
                  </p>
                  <div className="flex items-center justify-start md:justify-start gap-2">
                    <span className="text-xs font-normal text-[#1433cc] break-all">
                      {client?.externalUserId || "N/A"}
                    </span>
                    <div className="relative flex items-center">
                      <button
                        aria-label={t("myProfile.copyIdButton")}
                        className={`p-1 rounded transition-colors relative group ${
                          copiedVoxId
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-theme-primary hover:bg-blue-100"
                        }`}
                        onClick={() => {
                          if (client?.externalUserId && !copiedVoxId) {
                            copyToClipboard(
                              client.externalUserId,
                              setCopiedVoxId,
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            (e.key === "Enter" || e.key === " ") &&
                            client?.externalUserId &&
                            !copiedVoxId
                          ) {
                            e.preventDefault();
                            copyToClipboard(
                              client.externalUserId,
                              setCopiedVoxId,
                            );
                          }
                        }}
                        disabled={copiedVoxId || !client?.externalUserId}
                        tabIndex={0}
                      >
                        <CopyIcon className="w-4 h-4" />
                        {copiedVoxId && (
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10 animate-fade-in">
                            {t("myProfile.copiedToClipboard") || "Copied!"}
                            <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-800"></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="box-2 row-start-5 md:row-auto mt-0 md:mt-0">
              <Tabs
                value={searchParams.get("tab") || "my-cards"}
                onValueChange={(value) => {
                  if (value === 'cashback') {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'cta_click', ctaName: 'cashback_view' });
                  }
                  navigate(`?tab=${value}`, { replace: true })
                }}
                className="rounded-2xl mb-4 md:border space-y-4 md:border-[#ebeaea] md:rounded-[10px] md:rounded-bl-[20px] md:rounded-br-[20px] md:overflow-hidden shadow-xl"
              >
                <div className="bg-theme-neutral-foreground rounded-xl md:rounded-none shadow-sm p-1 flex items-center md:p-0">
                  <TabsList className="flex w-full h-12">
                    <TabsTrigger
                      value="my-cards"
                      className="flex-1 py-3 px-3 md:px-0 rounded-xl md:rounded-none text-[10px] font-semibold flex items-center justify-center hover:bg-theme-primary hover:text-theme-neutral transition-colors data-[state=active]:bg-theme-primary data-[state=active]:text-theme-neutral"
                      data-tour="cards-section"
                    >
                      <CreditCardIcon className="mr-2" />
                      {t("myProfile.myCardsTab")}
                    </TabsTrigger>

                    <TabsTrigger
                      value="cashback"
                      className="flex-1 py-3 px-3 md:px-0 rounded-xl md:rounded-none text-[10px] font-semibold flex items-center justify-center hover:bg-theme-primary hover:text-theme-neutral transition-colors data-[state=active]:bg-theme-primary data-[state=active]:text-theme-neutral"
                      data-tour="cashback-section"
                    >
                      <CashbackIcon className="mr-2" />
                      {t("myProfile.cashbackTab")}
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent
                  value="my-cards"
                  className="px-2 pb-2 md:px-2 md:pb-2"
                >
                  {/* Unenroll All Cards Link - Always visible if enrolled */}
                  {client?.isEnrolled && (
                    <div className="w-full flex justify-end mb-2">
                      <button
                        type="button"
                        onClick={handleUnenrollClick}
                        className="text-xs font-medium text-[hsla(0,62%,42%,1)] hover:text-red-600 underline block"
                      >
                        {t("myProfile.unenrollAll") || "Unenroll All Cards"}
                      </button>
                    </div>
                  )}

                  {cardsLoading ? (
                    <div className="bg-white flex flex-col items-center gap-2 py-8">
                      <p className="text-xs text-theme-secondary">
                        {t("myProfile.loadingCards")}
                      </p>
                    </div>
                  ) : cards.length === 0 ? (
                    <div className="bg-white flex flex-col items-center gap-2">
                      <img
                        src={CreditCardGeneric}
                        alt="card icon"
                        className="w-[33px] h-[33px]"
                      />
                      <p className="text-[10px] font-normal text-[#666666]">
                        {t("myProfile.noLinkedCards")}
                      </p>
                      <Button
                        id="home-btn"
                        variant="primary"
                        size="lg"
                        type="button"
                        onClick={handleExpressMiniClick}
                        disabled={isCardLimitReached || isExpressMiniLoading}
                      >
                        <div className="flex items-center gap-2">
                          <div>
                            <img
                              src={CardColor}
                              alt="card icon"
                              className="w-4 h-4 object-contain"
                            />
                          </div>
                          <span>{t("myProfile.linkCardButton")}</span>
                        </div>
                      </Button>
                      {t("myProfile.cardRegistrationWarning") && (
                        <p className="text-red-500 text-xs mt-2 text-left" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t("myProfile.cardRegistrationWarning")) }} />
                      )}
                    </div>
                  ) : (
                    <div className="bg-white flex flex-col items-center gap-3">
                      <div className="w-full flex flex-col gap-2 max-h-[420px] overflow-y-auto shadow-[0_8px_8px_-4px_rgba(0,0,0,0.1)]">
                        {cards.map((card) => (
                          <div
                            key={card.id}
                            className="w-full bg-white rounded-2xl px-4 py-4 shadow-[0_10px_30px_rgba(14,30,37,0.06)] border border-[hsla(0,0%,85%,1)] flex items-center justify-between"
                          >
                            <div className="flex items-center gap-4">
                              <img
                                src={VisaCard}
                                alt="card"
                                className="w-[35px] h-[22px] object-contain"
                              />
                              <div className="text-left">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-extrabold text-theme-secondary">
                                    {card.cardalias ||
                                      (tenant?.tenantcontentconfig?.cards
                                        ?.card_alias ||
                                        t("myProfile.cardLabel")) +
                                        " " +
                                        card.id}
                                  </div>
                                  {card.isdefault === 1 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                                      {t("common.default")}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs font-normal text-theme-secondary/80">
                                  •••• {card.last4digits}
                                </div>
                              </div>
                            </div>

                            <button
                              aria-label="open card"
                              className="p-2 rounded-full hover:bg-theme-primary/10 transition-colors"
                              onClick={() => handleCardClick(card)}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden
                                className="text-theme-primary"
                              >
                                <path
                                  d="M9 6l6 6-6 6"
                                  stroke="currentColor"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                      <Button
                        id="home-btn"
                        variant="primary"
                        size="lg"
                        type="button"
                        className="md:w-[220px] w-full"
                        onClick={handleExpressMiniClick}
                        disabled={isCardLimitReached || isExpressMiniLoading}
                      >
                        <div className="flex items-center gap-2">
                          <div>
                            <img
                              src={CardColor}
                              alt="card icon"
                              className="w-4 h-4 object-contain"
                            />
                          </div>
                          <span>{t("myProfile.linkCardButton")}</span>
                        </div>
                      </Button>
                      {t("myProfile.cardRegistrationWarning") && (
                        <p className="text-red-500 text-xs mt-2 text-left" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t("myProfile.cardRegistrationWarning")) }} />
                      )}
                    </div>
                  )}

                  <div className="my-4 text-xs text-gray-500 space-y-1 text-left">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          t("myProfile.disclaimer0") ||
                          "If you re-register a card that is already registered before cashback or rewards (e.g., raffle/roulette entries) are processed, previous transactions may become ineligible. Please ensure there are no pending rewards or cashback before re-registering your card.",
                          PROFILE_TEXT_HTML_PURIFY,
                        ),
                      }}
                    />
                    <p>
                      {t("myProfile.disclaimer1") ||
                        "*Your card information will be stored securely. You can register up to five cards."}
                    </p>
                    <p>
                      {t("myProfile.disclaimer2") ||
                        '*Currently, there is an issue preventing card registration using email addresses that contain special characters (such as "+"). Please use an email address that does not include special characters when registering.'}
                    </p>
                    <p>
                      {t("myProfile.disclaimer3") ||
                        "*Please make sure to enter the correct card number."}
                    </p>
                    <p>
                      {t("myProfile.disclaimer4") ||
                        "*If you are using the Sumitomo Mitsui Card Olive Flexible Pay, note that multiple card numbers may be issued. Be sure to register the card number that matches the one you intend to use. For more details, please "}
                      {t("myProfile.click") !== "" && (
                        <Link
                          to={`/${tenanturlname}/about`}
                          className="text-theme-primary underline"
                        >
                          &nbsp;{t("myProfile.click")}
                        </Link>
                      )}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="cashback">
                  <div className="bg-white rounded-2xl relative">
                    <div className="flex flex-col items-start mb-3 gap-2 px-4 pt-4">
                      {/* Title Row */}
                      <div className="flex justify-between items-center w-full">
                        <h2 className="text-base font-extrabold text-theme-secondary">
                          {t("myProfile.cashbackTab")}
                        </h2>
                        <button
                          className="text-theme-secondary hover:text-theme-primary transition-colors relative z-10"
                          onClick={() => {
                            // console.log("Filter button clicked!");
                            setIsFilterOpen(!isFilterOpen);
                          }}
                          aria-label="Filter"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                          </svg>
                        </button>
                      </div>

                      {/* Filter Bar */}
                      <div
                        className="w-full overflow-hidden transition-all duration-300 ease-in-out"
                        style={{
                          maxHeight: isFilterOpen ? "60px" : "0px",
                          opacity: isFilterOpen ? 1 : 0,
                        }}
                      >
                        <input
                          id="cashback-filter"
                          type="text"
                          name="cashback-filter"
                          value={cashbackFilter}
                          onChange={(e) => setCashbackFilter(e.target.value)}
                          className={`w-full h-12 appearance-none rounded-md border                    
      bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                          placeholder={t("myProfile.filterCashback")}
                        />
                      </div>

                      {/* Status */}
                      <div className="flex gap-4 mt-2">
                        <div className="flex items-center gap-2 text-xs text-theme-secondary">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                          <span>
                            {t("myProfile.statusSuccess") || "Success"}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-theme-secondary">
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" />
                          <span>
                            {t("myProfile.statusPending") || "In Process"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className=" border-t border-[hsla(0,0%,85%,1)] overflow-hidden max-h-[420px] overflow-y-auto ">
                      {rewardsList && rewardsList.length > 0 ? (
                        [...rewardsList]
                          .filter(
                            (reward) =>
                              reward.rewardStatus === "REWARD ISSUED" ||
                              reward.rewardStatus === "CREDIT PENDING" ||
                              reward.rewardStatus === "CREDIT SUCCESSFUL",
                          )
                          .filter((reward) => {
                            if (!cashbackFilter || !cashbackFilter.trim())
                              return true;
                            const searchTerm = cashbackFilter.trim();

                            // Calculate display name (same logic as render)
                            const isBaseReward = !reward.reward_id;
                            const displayName = isBaseReward
                              ? reward.merchantName
                              : getGamificationRewardDisplayName(reward);

                            // Search through all string and number properties
                            const searchableValues = [
                              reward.merchantName,
                              displayName, // Include translated name for Japanese search
                              reward.rewardAmount?.toString(),
                              reward.bonusAmount?.toString(),
                              reward.txAmount?.toString(),
                              reward.txDateUtc,
                              reward.bonusRewardDate,
                              reward.rewardStatus,
                              reward.last4digits,
                              reward.last_four,
                              reward.alias,
                              reward.gamificationType,
                              reward.reward_id?.toString(),
                              reward.transactionId,
                            ].filter(Boolean); // Remove null/undefined values

                            return searchableValues.some((value) => {
                              const valueStr = String(value);
                              // Case-insensitive search for English + exact match for Japanese
                              return (
                                valueStr
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase()) ||
                                valueStr.includes(searchTerm)
                              );
                            });
                          })
                          .sort((a, b) => {
                            const dateA = new Date(
                              a.reward_id ? a.bonusRewardDate : a.txDateUtc,
                            );
                            const dateB = new Date(
                              b.reward_id ? b.bonusRewardDate : b.txDateUtc,
                            );
                            return dateB - dateA; // newest first
                          })
                          .map((reward, index, filteredArray) => {
                            const isBaseReward = !reward.reward_id;
                            const isSuccess =
                              reward.rewardStatus === "CREDIT SUCCESSFUL" ||
                              reward.rewardStatus === "REWARD ISSUED";
                            const rewardDate = new Date(
                              isBaseReward
                                ? reward.txDateUtc
                                : reward.bonusRewardDate,
                            );
                            const decimalPlaces =
                              tenant?.tenantmasterconfig?.common
                                ?.decimal_places ?? 0;
                            const currencySymbol =
                              tenant?.tenantmasterconfig?.common
                                ?.transaction_currency_symbol || "¥";

                            const formattedDate = rewardDate.toLocaleDateString(
                              tenant?.tenantmasterconfig?.common?.local_code ||
                                "en-US",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              },
                            );
                            const formattedTime = rewardDate.toLocaleTimeString(
                              tenant?.tenantlanguagecontent
                                ?.languagecodedatecommon || "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              },
                            );

                            // Determine image source
                            let imageSrc = CreditCardGeneric;
                            let imageClassName = "w-[40px] h-[40px] object-cover";
                            if (isBaseReward) {
                              imageSrc =
                                reward.merchantImage || CreditCardGeneric;
                            } else {
                              imageSrc = getGamificationRewardImage(reward);
                              const gamificationType = String(
                                reward.gamificationType || "",
                              ).toUpperCase();
                              if (
                                getRewardOverviewImage(reward) ||
                                gamificationType === "SCRATCH_CARD" ||
                                gamificationType === "SPIN"
                              ) {
                                imageClassName =
                                  "w-[34px] h-[34px] object-contain";
                              }
                            }

                            // Determine display name
                            const displayName = isBaseReward
                              ? reward.merchantName
                              : getGamificationRewardDisplayName(reward);

                            const amount = isBaseReward
                              ? reward.rewardAmount
                              : reward.bonusAmount;

                            return (
                              <div key={reward.rewardId}>
                                <div
                                  className="flex items-center justify-between px-4 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                                  onClick={() => handleRewardClick(reward)}
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0 pr-3">
                                    <div className="h-10 rounded-full flex items-center justify-center overflow-hidden bg-gray-100 flex-shrink-0">
                                      <img
                                        src={imageSrc}
                                        alt={displayName}
                                        className={imageClassName}
                                        onError={(e) => {
                                          e.target.src = CreditCardGeneric;
                                        }}
                                      />
                                    </div>
                                    <div className="text-left flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-theme-secondary truncate">
                                        {displayName}
                                      </div>
                                      <div className="text-[10px] font-normal text-theme-accent">
                                        {formattedDate} {formattedTime}
                                      </div>
                                      <div className="text-[10px] font-normal text-gray-500 mt-0.5">
                                        {t("myProfile.transactionAmount")}:{" "}
                                        {currencySymbol}
                                        {parseFloat(
                                          reward.txAmount || 0,
                                        ).toFixed(decimalPlaces)}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-end gap-3 flex-shrink-0 w-[98px]">
                                    <div className="text-right min-w-0">
                                      <div className="text-[10px] font-normal text-gray-500 truncate">
                                        {t("myProfile.cashbackAmount")}
                                      </div>
                                      <div className="text-[15px] font-semibold text-theme-secondary">
                                        {currencySymbol}
                                        {parseFloat(amount).toFixed(
                                          decimalPlaces,
                                        )}
                                      </div>
                                    </div>
                                    <span
                                      className={`w-3.5 h-3.5 rounded-full inline-block ${
                                        isSuccess
                                          ? "bg-emerald-500"
                                          : "bg-yellow-400"
                                      }`}
                                    />
                                  </div>
                                </div>
                                {index < filteredArray.length - 1 && (
                                  <div className="border-t border-gray-200" />
                                )}
                              </div>
                            );
                          })
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 px-4">
                          <img
                            src={CreditCardGeneric}
                            alt="no rewards"
                            className="w-[33px] h-[33px] mb-2"
                          />
                          <p className="text-xs font-semibold text-theme-secondary mb-1">
                            {t("myProfile.noRewards")}
                          </p>
                          <p className="text-[10px] font-normal text-[#666666] text-center">
                            {t("myProfile.noRewardsDescription")}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <div className="contents md:block space-y-0 md:space-y-3">
            {showCashbackSummary && (
              <>
                {/* Total Cashback Card */}
                <div className="box-3 row-start-4 md:row-auto">
                  <div
                    className="bg-white rounded-2xl px-6 pt-4 pb-1 shadow-xl border border-[hsla(0,0%,85%,1)]"
                    data-tour="total-cashback-widget"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] font-bold text-theme-secondary">
                          {t("myProfile.totalCashback")}
                        </p>
                      </div>

                      <div className="text-right flex items-center gap-2">
                        <p className="text-[20px] font-bold leading-tight text-theme-secondary">
                          {parseFloat(cBBaseReward + cBBonusReward).toFixed(
                            tenant?.tenantmasterconfig?.common?.decimal_places || 0,
                          )}
                        </p>
                        <span className="text-[10px] font-semibold text-theme-secondary">
                          {tenant?.tenantmasterconfig?.common?.currency_symbol ||
                            t("myProfile.currency")}
                        </span>
                      </div>
                    </div>

                    <div className="my-2 py-2 border-t border-gray-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-semibold text-theme-secondary">
                          {t("myProfile.baseCashback")}
                        </div>
                        <div className="text-[15px] font-bold text-theme-secondary">
                          {parseFloat(cBBaseReward).toFixed(
                            tenant?.tenantmasterconfig?.common?.decimal_places || 0,
                          )}{" "}
                          <span className="text-[10px] font-semibold text-theme-secondary">
                            {tenant?.tenantmasterconfig?.common?.currency_symbol ||
                              t("myProfile.currency")}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-semibold text-theme-secondary">
                          {t("myProfile.bonusCashback")}
                        </div>
                        <div className="text-[15px] font-bold text-theme-secondary">
                          {parseFloat(cBBonusReward).toFixed(
                            tenant?.tenantmasterconfig?.common?.decimal_places || 0,
                          )}{" "}
                          <span className="text-[10px] font-semibold text-theme-secondary">
                            {tenant?.tenantmasterconfig?.common?.currency_symbol ||
                              t("myProfile.currency")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Campaign banner carousel — images from getCampaigns (same keys as getCampaigns) */}
            {campaignBannerSlides.length > 0 ? (
              <div className="box-4 row-start-2 md:row-auto">
                <div className="w-full">
                  <div
                    ref={campaignBannerTrackRef}
                    onScroll={onCampaignBannerScroll}
                    onMouseEnter={pauseCampaignBanner}
                    onMouseLeave={resumeCampaignBanner}
                    onPointerEnter={pauseCampaignBanner}
                    onPointerLeave={resumeCampaignBanner}
                    onPointerDown={pauseCampaignBanner}
                    onPointerUp={resumeCampaignBanner}
                    onPointerCancel={resumeCampaignBanner}
                    onTouchStart={pauseCampaignBanner}
                    onTouchEnd={resumeCampaignBanner}
                    onTouchCancel={resumeCampaignBanner}
                    className="flex w-full overflow-x-auto snap-x snap-mandatory bg-white [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    style={{
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    {campaignBannerSlides.map((slide) => {
                      const canNavigate = Boolean(
                        slide.campaignId && tenant?.tenanturlname,
                      );
                      const slideType = String(slide.type || "").toUpperCase();
                      const navigateToSlideCampaign = () => {
                        if (!canNavigate) return;
                        const base = `/${tenant.tenanturlname}`;
                        if (slideType === "CHALLENGE") {
                          navigate(`${base}/gamification/stamprally/${slide.campaignId}`);
                          return;
                        }
                        navigate(`${base}/gamification/spinwheel/${slide.campaignId}`);
                      };

                      return (
                        <div
                          key={slide.key}
                          className="w-full min-w-0 flex-none snap-center snap-always"
                        >
                          <div
                            className={`campaign-banner ${
                              canNavigate ? "cursor-pointer" : "cursor-default"
                            }`}
                            onClick={navigateToSlideCampaign}
                            onKeyDown={(e) => {
                              if (!canNavigate) return;
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigateToSlideCampaign();
                              }
                            }}
                            role={canNavigate ? "button" : undefined}
                            tabIndex={canNavigate ? 0 : undefined}
                          >
                            <div className="w-full overflow-hidden relative rounded-2xl border border-[hsla(0,0%,85%,1)] bg-white">
                              <img
                                src={slide.mobile}
                                alt={t("common.bannerAlt") || "Banner"}
                                className="block md:hidden w-full h-auto max-h-none object-contain align-middle"
                                loading="eager"
                                onError={(e) => {
                                  e.target.src = require("../assets/images/backgrounds/campaign-1.png");
                                }}
                              />
                              <img
                                src={slide.desktop}
                                alt={t("common.bannerAlt") || "Banner"}
                                className="hidden md:block w-full h-auto max-h-none object-contain align-middle"
                                loading="eager"
                                onError={(e) => {
                                  e.target.src = require("../assets/images/backgrounds/campaign-1.png");
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {campaignBannerSlides.length > 1 && (
                    <div className="mt-2 flex items-center justify-center gap-2">
                      {campaignBannerSlides.map((slide, i) => (
                        <button
                          key={`dot-${slide.key}`}
                          type="button"
                          aria-label={`${t("common.bannerAlt") || "Banner"} ${i + 1}`}
                          onClick={() => scrollCampaignBannerTo(i)}
                          className="h-2 w-2 rounded-full transition-colors"
                          style={{
                            backgroundColor:
                              i === campaignBannerActiveIndex
                                ? "hsl(var(--primary))"
                                : "#D9D9D9",
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              ""
            )}

            {availableGamificationTypes.length > 0 && activeGamificationType ? (
              (() => {
                const gamificationConfig = {
                  SPIN: {
                    notifyIcon: SpinNotifyIcon,
                    titleIcon: SpinTitleIcon,
                    title: "",
                    countLabel: (count) =>
                      t("common.spinsRemaining").replace("{count}", count),
                    buttonLabel: tOr(t, "common.play", "Play"),
                    getRoute: (id) =>
                      `/${tenanturlname}/gamification/spinwheel/${id}`,
                  },
                  CHALLENGE: {
                    notifyIcon: ScratchNotifyIcon,
                    titleIcon: ScratchTitleIcon,
                    title: tOr(
                      t,
                      "gamification.stampRallyCampaignsTitle",
                      "Stamp Rally Campaigns",
                    ),
                    countLabel: (count) => `${count} ${tOr(t, "common.chancesRemaining", "chances remaining")}`,
                    buttonLabel: tOr(t, "gamification.viewProgress", "View"),
                    getRoute: (id) =>
                      `/${tenanturlname}/gamification/stamprally/${id}`,
                  },
                };
                const activeConfig = gamificationConfig[activeGamificationType];
                const campaignRows =
                  activeGamificationType === "CHALLENGE"
                    ? challengeCampaigns
                    : spinCampaigns;
                const showGamificationTabs = availableGamificationTypes.length > 1;

                return (
                  <div className="box-5 row-start-3 md:row-auto">
                    <div className="bg-white rounded-2xl shadow-lg border border-[hsla(0,0%,85%,1)] overflow-hidden">
                      {showGamificationTabs && (
                        <div className="relative bg-white py-4 border-b border-[hsla(0,0%,88%,1)]">
                          <div
                            ref={gamTabStripRef}
                            className="flex gap-6 justify-center"
                          >
                            {availableGamificationTypes.map((type) => {
                              const config = gamificationConfig[type];
                              const count =
                                type === "CHALLENGE"
                                  ? challengeCampaigns.length
                                  : spinCampaigns.length;
                              const isActive = activeGamificationType === type;
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  data-gam-tab={type}
                                  onClick={() => setActiveGamificationType(type)}
                                  className="relative flex flex-col items-center"
                                  aria-label={config.title || type}
                                >
                                  <div
                                    className={`w-[48px] h-[48px] rounded-full flex items-center justify-center transition-all ${
                                      isActive
                                        ? "bg-[#CFE6FF] shadow-md"
                                        : "bg-[#DCEEFF]"
                                    }`}
                                  >
                                    <img
                                      src={config.notifyIcon}
                                      alt={config.title || type}
                                      className="w-[42px] h-[42px]"
                                    />
                                  </div>
                                  {count > 0 && (
                                    <span className="absolute -top-1 -right-1 z-10 box-border flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#FF3B30] px-1 text-[12px] font-semibold leading-none text-white shadow-[0_2px_6px_rgba(0,0,0,0.18)] tabular-nums">
                                      {count}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {activeConfig && campaignRows.length > 0 && (
                        <div
                          ref={gamContentRef}
                          className="bg-[#F6F7F9] text-left relative"
                        >
                          {showGamificationTabs && (
                            <span
                              aria-hidden="true"
                              className="absolute -top-[9px] w-4 h-4 border border-[hsla(0,0%,85%,1)] rotate-45"
                              style={{
                                borderBottom: "none",
                                borderRight: "none",
                                backgroundColor: "#F6F7F9",
                                left:
                                  gamCaretLeftPx != null
                                    ? `${gamCaretLeftPx}px`
                                    : "50%",
                                transform: "translateX(-50%) rotate(45deg)",
                              }}
                            />
                          )}

                          {activeConfig.title && (
                            <div className="px-4 py-3 bg-[#F6F7F9]">
                              <h3 className="text-left text-[14px] font-bold leading-tight text-theme-secondary">
                                {activeConfig.title}
                              </h3>
                            </div>
                          )}

                          <div className="bg-[#F6F7F9] border-t border-b border-[hsla(0,0%,88%,1)] divide-y divide-[hsla(0,0%,88%,1)] max-h-[360px] overflow-y-auto">
                            {campaignRows.map((campaign, index) => {
                              const rowCampaignId =
                                getCampaignId(campaign) ||
                                (activeGamificationType === "CHALLENGE"
                                  ? scratchCampaignId
                                  : campaignId);
                              const chanceCount = getCampaignChanceCount(
                                campaign,
                                activeGamificationType,
                              );
                              const expiryText = getCampaignExpiryText(campaign);
                              const route = rowCampaignId
                                ? activeConfig.getRoute(rowCampaignId)
                                : null;

                              return (
                                <div
                                  key={`${activeGamificationType}-${rowCampaignId || index}`}
                                  className="flex items-center justify-between gap-3 px-3 py-3"
                                >
                                  <div className="flex min-w-0 items-start gap-3">
                                    <img
                                      src={activeConfig.titleIcon}
                                      alt={getCampaignTitle(campaign)}
                                      className="h-8 w-8 shrink-0 object-contain mt-1"
                                    />
                                    <div className="min-w-0 text-left">
                                      <div className="text-[13px] font-semibold text-black leading-tight break-words">
                                        {getCampaignTitle(campaign)}
                                      </div>
                                      <div className="text-xs font-medium text-[#0068B4]">
                                        {activeConfig.countLabel(chanceCount)}
                                      </div>
                                      {expiryText && (
                                        <div className="text-[10px] font-medium text-[#FF4823] leading-tight">
                                          {expiryText}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => route && navigate(route)}
                                    disabled={!route}
                                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-full min-w-[60px] h-[26px] px-2 text-sm flex items-center justify-center focus:outline-none shrink-0"
                                  >
                                    <svg
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                      aria-hidden="true"
                                    >
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                    {activeConfig.buttonLabel}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="box-6 row-start-6 md:row-auto hidden md:block">
                <div
                  className="py-20 flex items-center justify-center border rounded-2xl shadow-xl border border-[hsla(0,0%,85%,1)]"
                  style={{ paddingBottom: "5.8rem" }}
                >
                  <div className="inner-banner-nr flex items-center justify-center flex-col max-w-[260px] gap-2">
                    {noResultImageUrl ? (
                      <img
                        src={noResultImageUrl}
                        alt="No Result"
                        className="h-[138px] object-contain"
                      />
                    ) : (
                      <svg
                        width="120"
                        height="120"
                        viewBox="0 0 120 120"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        {/* Outer dashed ring */}
                        <circle
                          cx="60"
                          cy="60"
                          r="54"
                          stroke="#E5E7EB"
                          strokeWidth="2"
                          strokeDasharray="7 4"
                        />
                        {/* Wheel spokes */}
                        <line
                          x1="60"
                          y1="18"
                          x2="60"
                          y2="42"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="60"
                          y1="78"
                          x2="60"
                          y2="102"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="18"
                          y1="60"
                          x2="42"
                          y2="60"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="78"
                          y1="60"
                          x2="102"
                          y2="60"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="29"
                          y1="29"
                          x2="46"
                          y2="46"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="74"
                          y1="74"
                          x2="91"
                          y2="91"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="91"
                          y1="29"
                          x2="74"
                          y2="46"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <line
                          x1="46"
                          y1="74"
                          x2="29"
                          y2="91"
                          stroke="#D1D5DB"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        {/* Outer wheel ring */}
                        <circle
                          cx="60"
                          cy="60"
                          r="34"
                          stroke="#E5E7EB"
                          strokeWidth="3"
                          fill="none"
                        />
                        {/* Hub */}
                        <circle
                          cx="60"
                          cy="60"
                          r="10"
                          fill="#F3F4F6"
                          stroke="#D1D5DB"
                          strokeWidth="2"
                        />
                        {/* Diagonal cross-out */}
                        <line
                          x1="36"
                          y1="36"
                          x2="84"
                          y2="84"
                          stroke="#F87171"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          opacity="0.6"
                        />
                        <line
                          x1="84"
                          y1="36"
                          x2="36"
                          y2="84"
                          stroke="#F87171"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          opacity="0.6"
                        />
                      </svg>
                    )}
                    <p className="text-[#686868] font-semibold text-sm">
                      {t("common.noSpins")}
                    </p>
                    <p className="text-[#686868] font-normal text-xs">
                      {t("common.noSpinsDescription")}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Stamp Rally Banner (CHALLENGE) */}
            <div className="box-7 row-start-7 md:row-auto hidden">
              {scratchCampaignId && availableScratch > 0 && (
                <div
                  className="break-inside-avoid mb-4 campaign-banner cursor-pointer mt-2"
                  onClick={() => {
                    if (tenant?.tenanturlname) {
                      navigate(
                        `/${tenant.tenanturlname}/gamification/stamprally/${scratchCampaignId}`,
                      );
                    }
                  }}
                >
                  <div className="w-full overflow-hidden relative">
                    {/* Mobile banner */}
                    <img
                      src={
                        scratchCampaignBanner?.mobile ||
                        require("../assets/images/backgrounds/spin-banner-feb.png")
                      }
                      alt={t("common.bannerAlt") || "Banner"}
                      className="block md:hidden w-full h-[126px] object-cover"
                      loading="lazy"
                      onError={(e) => {
                        console.error(
                          "❌ CHALLENGE Mobile banner failed to load:",
                          scratchCampaignBanner?.mobile,
                        );
                        e.target.src = require("../assets/images/backgrounds/spin-banner-feb.png");
                      }}
                      onLoad={() => {}}
                    />
                    {/* Desktop banner */}
                    <img
                      src={
                        scratchCampaignBanner?.desktop ||
                        require("../assets/images/backgrounds/spin-banner-feb.png")
                      }
                      alt={t("common.bannerAlt") || "Banner"}
                      className="hidden md:block w-full h-[151px] object-cover"
                      loading="lazy"
                      onError={(e) => {
                        console.error(
                          "❌ CHALLENGE Desktop banner failed to load:",
                          scratchCampaignBanner?.desktop,
                        );
                        e.target.src = require("../assets/images/backgrounds/spin-banner-feb.png");
                      }}
                      onLoad={() => {}}
                    />
                    <div className="absolute bottom-[20px] left-1/2 -translate-x-1/2">
                      <div className="bg-white rounded-[30px] h-[26px] w-[220px] p-2 flex items-center justify-center shadow-md">
                        <div className="flex items-center gap-2">
                          <span>
                            <img
                              src={require("../assets/images/backgrounds/circle.png")}
                              alt="Stamp Rally"
                              className="w-4 h-4"
                            />
                          </span>
                          <span className="text-[10px] font-semibold text-theme-secondary">
                            Stamp Rally
                          </span>
                        </div>

                        <span className="text-[10px] font-semibold text-theme-sub-title">
                          {availableScratch > 0 ? (
                            <>
                              <span>{availableScratch}</span> remaining
                            </>
                          ) : (
                            "View"
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Details Modal */}
      <Modal
        open={isCardModalOpen}
        onClose={closeCardModal}
        headerImage={VisaCard}
      >
        {selectedCard && (
          <div className="w-full max-w-sm">
            <div className="flex flex-col items-center mb-6">
              <h2 className="text-lg font-bold text-theme-primary mb-1 flex items-center gap-2">
                {selectedCard.cardalias || `My Card ${selectedCard.id}`}
                <button
                  className="text-theme-primary hover:text-theme-primary/80"
                  aria-label="Edit card name"
                  onClick={() => {
                    setAliasCardNumber(selectedCard.cardnumber);
                    setCardAlias(selectedCard.cardalias || "");
                    setIsCardModalOpen(false);
                    setShowCardAliasModal(true);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </h2>
            </div>

            <div className="space-y-4 text-left mb-6">
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.cardNumberLabel")}
                </div>
                <div className="text-sm font-normal text-theme-secondary">
                  {t("myProfile.visaPrefix")} {selectedCard.last4digits}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.registeredOn")}
                </div>
                <div className="text-sm font-normal text-theme-secondary">
                  {new Date(selectedCard.created_at).toLocaleDateString(
                    "en-US",
                    {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    },
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {selectedCard.isdefault !== 1 && (
                <Button
                  variant="primary"
                  size="lg"
                  type="button"
                  onClick={() => {
                    setDefaultCard(selectedCard);
                    closeCardModal();
                  }}
                >
                  {t("myProfile.setAsDefaultCard")}
                </Button>
              )}

              {selectedCard.isdefault === 1 && (
                <div className="text-sm font-medium text-green-600 text-center py-2">
                  {t("myProfile.defaultCardMessage")}
                </div>
              )}

              {selectedCard.isdefault !== 1 && (
                <button
                  onClick={handleDeleteClick}
                  className="text-xs font-medium text-[hsla(0,62%,42%,1)] hover:text-red-600 underline block w-full"
                >
                  {t("myProfile.unregisterCard")}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmModal}
        onClose={() => setDeleteConfirmModal(false)}
        type="confirm"
        title={t("myProfile.removeCardTitle")}
        message={t("myProfile.removeCardMessage").replace(
          "{last4}",
          selectedCard?.last4digits || "",
        )}
        onConfirm={handleConfirmDelete}
        confirmText={t("myProfile.yesRemove")}
        cancelText={t("common.cancel")}
      />

      {/* Unenroll Confirmation Modal */}
      <Modal
        open={unenrollConfirmModal}
        onClose={() => setUnenrollConfirmModal(false)}
        type="confirm"
        title={t("myProfile.unenrollTitle")}
        message={t("myProfile.unenrollMessage")}
        onConfirm={handleConfirmUnenroll}
        confirmText={t("myProfile.yesUnenroll")}
        cancelText={t("common.cancel")}
      />

      {/* Status Modal (Success/Error/Info) */}
      <Modal
        open={statusModal.open}
        onClose={closeStatusModal}
        type={statusModal.type}
        title={
          statusModal.type === "success"
            ? t("common.success")
            : statusModal.type === "error"
              ? t("common.error")
              : t("common.information")
        }
        message={statusModal.message}
        renderAsHtml={statusModal.renderAsHtml}
      />

      {/* Reward Details Modal */}
      <Modal
        open={isRewardModalOpen}
        onClose={closeRewardModal}
        headerImage={rewardDetailsHeaderImage}
        showHeaderImage={true}
      >
        {selectedReward && (
          <div className="w-full max-w-sm">
            <div className="flex flex-col items-center mb-6">
              <h2 className="text-lg font-bold text-theme-primary mb-1">
                {t("myProfile.rewardDetails")}
              </h2>
            </div>

            <div className="space-y-4 text-left mb-6">
              {/* Transaction ID */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.transactionId")}:
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-normal text-theme-secondary flex-1 break-all">
                    {selectedReward.originalTxId ||
                      selectedReward.transactionId ||
                      t("notAvailable")}
                  </div>
                  <div className="relative flex items-center">
                    <button
                      aria-label="Copy Transaction ID"
                      className={`p-1 rounded transition-colors flex-shrink-0 relative group ${
                        copiedTransactionId
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-theme-primary hover:bg-blue-100"
                      }`}
                      onClick={() => {
                        const txId =
                          selectedReward.originalTxId ||
                          selectedReward.transactionId;
                        if (
                          txId &&
                          txId !== t("notAvailable") &&
                          !copiedTransactionId
                        ) {
                          copyToClipboard(txId, setCopiedTransactionId);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          const txId =
                            selectedReward.originalTxId ||
                            selectedReward.transactionId;
                          if (
                            txId &&
                            txId !== t("notAvailable") &&
                            !copiedTransactionId
                          ) {
                            copyToClipboard(txId, setCopiedTransactionId);
                          }
                        }
                      }}
                      disabled={
                        copiedTransactionId ||
                        !selectedReward.transactionId ||
                        selectedReward.transactionId === t("notAvailable")
                      }
                      tabIndex={0}
                    >
                      <CopyIcon className="w-4 h-4" />
                      {copiedTransactionId && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10 animate-fade-in">
                          {t("myProfile.copiedToClipboard") || "Copied!"}
                          <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-800"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Merchant/Campaign */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.merchantCampaign")}:
                </div>
                <div className="text-sm font-normal text-theme-secondary">
                  {selectedReward.reward_id
                    ? getGamificationRewardDisplayName(selectedReward)
                    : selectedReward.merchantName}
                </div>
              </div>

              {/* Transaction Date */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.transactionDate")}:
                </div>
                <div className="text-sm font-normal text-theme-secondary">
                  {new Date(
                    selectedReward.txDateUtc,
                  ).toLocaleDateString(
                    tenant?.tenantmasterconfig?.common?.local_code || "en-US",
                    { year: "numeric", month: "2-digit", day: "2-digit" },
                  )}{" "}
                  {new Date(
                    selectedReward.txDateUtc,
                  ).toLocaleTimeString(
                    tenant?.tenantlanguagecontent?.languagecodedatecommon ||
                      "en-US",
                    { hour: "2-digit", minute: "2-digit", hour12: true },
                  )}
                </div>
              </div>

              {/* Card Info */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.cardLabel")}:
                </div>
                <div className="text-sm font-normal text-theme-secondary">
                  {selectedReward.alias
                    ? selectedReward.alias
                    : `**** ${
                        selectedReward.cardLastFour || selectedReward.last_four
                      }`}
                </div>
              </div>

              {/* Transaction Amount */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.transactionAmount")}:
                </div>
                <div className="text-sm font-normal text-theme-secondary">
                  {tenant?.tenantmasterconfig?.common
                    ?.transaction_currency_symbol || selectedReward.txCurrency}
                  {parseFloat(selectedReward.txAmount).toFixed(
                    tenant?.tenantmasterconfig?.common?.decimal_places || 0,
                  )}
                  {selectedReward.isEligible && (
                    <span className="ml-2 inline-block px-2 py-1 text-[10px] font-semibold bg-gray-200 text-gray-700 rounded">
                      {t("myProfile.qualified")}
                    </span>
                  )}
                </div>
              </div>

              {/* Cashback Amount */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.cashbackAmount")}:
                </div>
                <div className="text-sm font-bold text-theme-primary">
                  {tenant?.tenantmasterconfig?.common
                    ?.transaction_currency_symbol || "¥"}
                  {parseFloat(
                    selectedReward.reward_id
                      ? selectedReward.bonusAmount
                      : selectedReward.rewardAmount,
                  ).toFixed(
                    tenant?.tenantmasterconfig?.common?.decimal_places || 0,
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <div className="text-xs font-semibold text-theme-secondary mb-1">
                  {t("myProfile.statusLabel")}:
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full inline-block ${
                      selectedReward.rewardStatus === "CREDIT SUCCESSFUL" ||
                      selectedReward.rewardStatus === "REWARD ISSUED"
                        ? "bg-emerald-500"
                        : "bg-yellow-400"
                    }`}
                  />
                  <span className="text-sm font-normal text-theme-secondary">
                    {selectedReward.rewardStatus === "CREDIT SUCCESSFUL"
                      ? t("myProfile.statusSuccess")
                      : t("myProfile.statusPending")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Card Alias Modal */}
      <Modal
        open={showCardAliasModal}
        onClose={() => {
          setShowCardAliasModal(false);
          // User dismissed alias modal without saving — still trigger new_variant popup if pending
          if (pendingNewVariantRef.current) {
            const assignment = pendingNewVariantRef.current;
            pendingNewVariantRef.current = null;
            if (
              !isABTestHandledThisSession(assignment.test_id) &&
              acquireABTestLock(assignment.test_id, "popup")
            ) {
              showNewVariantPopup(assignment);
            }
          }
        }}
        headerImage={VisaCard}
      >
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-6">
            <h2 className="text-lg font-bold text-theme-primary mb-1">
              {t("myProfile.nameYourCard") || "Name Your Card"}
            </h2>
            <p className="text-xs text-theme-secondary text-center">
              {t("myProfile.nameYourCardDescription") ||
                "Give your card a name to easily identify it"}
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="text-xs font-semibold text-theme-secondary mb-2 block">
                {t("myProfile.cardName") || "Card Name"}:
              </label>
              <input
                type="text"
                value={cardAlias}
                onChange={(e) => setCardAlias(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-primary"
                placeholder={t("myProfile.enterCardName") || "Enter card name"}
                maxLength={15}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSaveCardAlias}
              variant="primary"
              size="default"
              className="w-full h-10"
              disabled={!cardAlias.trim()}
            >
              {t("myProfile.save") || "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Loading Overlay */}
      {(isLoading || isEnrolling || isExpressMiniLoading) && (
        <Loader
          fullscreen
          label={
            isExpressMiniLoading
              ? "Refreshing session..."
              : t("myProfile.processing")
          }
          size="xl"
        />
      )}

      {/* NPS Feedback Modal */}
      {showNPS && (
        <FeedbackModal
          open={showNPS}
          onClose={() => setShowNPS(false)}
          triggerType="cashback_history"
          channel="IN_APP"
          npsData={{ totalCashback: cBBaseReward + cBBonusReward }}
        />
      )}

      {/* AB Test popup — new user (shows after first card add) */}
      {newVariantAssignment && !tourActive && (
        <ABTestPopup
          open={!!newVariantAssignment}
          assignment={newVariantAssignment}
          impressionAction="card_enrollment"
          onClose={(action) => {
            if (action === "View Tour") {
              const assignment = newVariantAssignment;
              setNewVariantAssignment(null);
              window.setTimeout(() => {
                if (!startTour(assignment)) {
                  markABTestHandledThisSession(assignment?.test_id);
                  releaseABTestLock(assignment?.test_id);
                }
              }, 0);
              return;
            }
            if (action === "Maybe Later") {
              dismissForSession(newVariantAssignment?.test_id);
            }
            markABTestHandledThisSession(newVariantAssignment?.test_id);
            releaseABTestLock(newVariantAssignment?.test_id);
            setNewVariantAssignment(null);
          }}
        />
      )}

      {/* AB Test Guided Tour */}
      {tourActive && <GuidedTour isProfileLoading={isLoading} />}
    </>
  );
}
